import { Location } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';

import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { AlertCardComponent } from '../../../shared/components/alert-card/alert-card.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import {
    PageScrollSpyComponent,
    ScrollSpySection,
} from '../../../shared/components/page-scroll-spy/page-scroll-spy.component';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { VendaTipoDocumentoEnum } from '../../../shared/enums/vendas/venda-tipo-documento.enum';
import { TipoNumeracaoDocumentoEnum } from '../../../shared/enums/vendas/tipo-numeracao-documento.enum';
import { TipoExibicaoSecaoPedidoEnum } from '../../../shared/enums/vendas/tipo-exibicao-secao-pedido.enum';
import { VendaTipoIntegracaoEnum, VendaTipoIntegracaoIndicador } from '@shared/enums/vendas/venda-tipo-integracao.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { VendaDocumentoService } from '../../../shared/services/apis/vendas/venda-documento.service';
import { ProcessoVendaService } from '../../../shared/services/apis/vendas/processo-venda.service';
import { MoverVendaEtapaRequest } from '../../../shared/services/requests/vendas/mover-venda-etapa.request';
import { MotivoTransicao } from '../../../shared/services/models/vendas/motivo-transicao.model';
import { SalvarVendaRequest } from '../../../shared/services/requests/vendas/salvar-venda.request';
import { ConfigOrcamento } from '../../../shared/services/models/vendas/config-orcamento.model';
import { ConfigPedidoVenda } from '../../../shared/services/models/vendas/config-pedido-venda.model';
import { ConfigVendasGeralStruct } from '../../../shared/services/structs/vendas/config-vendas-geral.struct';
import { ConfigVendasGeral } from '../../../shared/services/models/vendas/config-vendas-geral.model';
import { ConfigFinancasStruct } from '../../../shared/services/structs/vendas/config-financas.struct';
import { TagVenda } from '../../../shared/services/models/vendas/tag-venda.model';
import { ClienteDetalheStruct } from '../../../shared/services/structs/vendas/cliente-detalhe.struct';
import { HistoricoClienteStruct } from '@shared/services/structs/vendas/historico-cliente.struct';
import { ClienteVendaSelectStruct } from '../../../shared/services/structs/pessoa/cliente-venda-select.struct';
import { VendaEscopoStruct } from '../../../shared/services/structs/vendas/venda-escopo.struct';
import { KanbanCor } from '../../../shared/services/models/vendas/kanban-cor.model';
import { KanbanIcone } from '../../../shared/services/models/vendas/kanban-icone.model';
import { KanbanProcessoStruct } from '../../../shared/services/structs/vendas/kanban-processo.struct';
import { EtapaKanbanStruct } from '../../../shared/services/structs/vendas/etapa-kanban.struct';
import { AuthService } from '../../../shared/services/apis/usuario/auth.service';
import { ThemeService } from '@shared/services/utils/theme.service';
import { MaskService } from '../../../shared/services/mask.service';
import { LookupService as ProdutoLookupService } from '../../../shared/services/apis/produto/produto-lookup.service';
import { MotorFiscalLookupService } from '../../../shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { TipoOperacaoEnum } from '../../../shared/enums/motor-fiscal/tipo-operacao.enum';
import { TipoPadraoNaturezaEnum } from '../../../shared/enums/motor-fiscal/tipo-padrao-natureza.enum';
import { LocalizacaoEstoque } from '../../../shared/services/models/produto/localizacao-estoque.model';
import { FormaEnvioSelectStruct } from '../../../shared/services/structs/vendas/forma-envio-select.struct';
import { NgxCurrencyConfig } from 'ngx-currency';
import { VendedorVendaStruct } from '../../../shared/services/structs/vendas/vendedor-venda.struct';
import { VendaAnexoSalvarStruct } from '../../../shared/services/structs/vendas/venda-anexo-salvar.struct';
import { VendaDetalheStruct } from '../../../shared/services/structs/vendas/venda-detalhe.struct';
import {
    acoesOportunidade,
    formatarNumeroDocumento,
    rotuloDocumento,
    PvAcoesOportunidadeVm,
    PvPermissoesOportunidade,
    resolverPermissoesOportunidade,
    TOOLTIP_OPORTUNIDADE_OUTRO_VENDEDOR,
    PV_TEXTO_EXCLUSAO_COM_OPORTUNIDADE,
} from '../processo-venda/processo-venda.models';
import { ExcluirVendaRequest } from '../../../shared/services/requests/vendas/excluir-venda.request';
import { PvCorVm, resolverCor } from '@shared/helpers/kanban-cor.helper';
import { PermissaoEnum } from '../../../shared/enums/usuario/permissao.enum';
import { TipoContatoEnum } from '@shared/enums/pessoa/tipo-contato.enum';
import { PessoaService } from '@shared/services/apis/pessoa/pessoa.service';
import { PessoaLookupService } from '@shared/services/apis/pessoa/pessoa-lookup.service';
import { OportunidadeService } from '@shared/services/apis/vendas/oportunidade.service';
import { OportunidadeKanbanItemComValorStruct } from '@shared/services/structs/vendas/oportunidade-kanban-item-com-valor.struct';
import { VendaTipoBloqueioEnum } from '@shared/enums/vendas/venda-tipo-bloqueio.enum';
import { TipoEtapaKanbanEnum } from '../../../shared/enums/vendas/tipo-etapa-kanban.enum';
import { CriarModeloVendaRequest } from '../../../shared/services/requests/vendas/criar-modelo-venda.request';
import { PvMotivoDialogComponent, PvMotivoDialogData, PvMotivoDialogResult } from '../processo-venda/dialogs/pv-motivo-dialog/pv-motivo-dialog.component';
import {
    PvConfirmarMovimentoDialogComponent,
    PvConfirmarMovimentoDialogData,
    PvConfirmarMovimentoDialogResult,
    cabecalhoDoMovimento,
} from '../processo-venda/dialogs/pv-confirmar-movimento-dialog/pv-confirmar-movimento-dialog.component';
import { MoverVendaEtapaErrorCodeEnum } from '@shared/enums/vendas/mover-venda-etapa-error-code.enum';
import { PvLancarFinanceiroDialogComponent, PvLancarFinanceiroDialogData } from '../processo-venda/dialogs/pv-lancar-financeiro-dialog/pv-lancar-financeiro-dialog.component';
import { PvExcluirFinanceiroDialogComponent, PvExcluirFinanceiroDialogData } from '../processo-venda/dialogs/pv-excluir-financeiro-dialog/pv-excluir-financeiro-dialog.component';
import {
    PvLancarEstoqueDialogComponent,
    PvLancarEstoqueDialogData,
} from '../processo-venda/dialogs/pv-lancar-estoque-dialog/pv-lancar-estoque-dialog.component';
import {
    PvExcluirEstoqueDialogComponent,
    PvExcluirEstoqueDialogData,
} from '../processo-venda/dialogs/pv-excluir-estoque-dialog/pv-excluir-estoque-dialog.component';
import { PvFinanceiroDialogComponent, PvFinanceiroDialogData } from '../processo-venda/dialogs/pv-financeiro-dialog/pv-financeiro-dialog.component';
import { CampoContasReceberEnum } from '@shared/enums/financas/campo-contas-receber.enum';
import {
    OportunidadesClienteDialogComponent,
    OportunidadesClienteDialogData,
} from '@shared/components/oportunidades-cliente-dialog/oportunidades-cliente-dialog.component';
import {
    VincularOportunidadeDialogComponent,
    VincularOportunidadeDialogData,
    VincularOportunidadeDialogResult,
} from '@shared/components/vincular-oportunidade-dialog/vincular-oportunidade-dialog.component';
import {
    DesvincularOportunidadeDialogComponent,
    DesvincularOportunidadeDialogData,
} from '@shared/components/desvincular-oportunidade-dialog/desvincular-oportunidade-dialog.component';
import {
    CriarOportunidadeDocumentoDialogComponent,
    CriarOportunidadeDocumentoDialogData,
    CriarOportunidadeDocumentoDialogResult,
} from '@shared/components/criar-oportunidade-documento-dialog/criar-oportunidade-documento-dialog.component';
import { VdLiberacaoDialogComponent, VdLiberacaoDialogData, VdLiberacaoDialogResult } from './components/vd-liberacao-dialog/vd-liberacao-dialog.component';
import { VdImpressaoDialogComponent, VdImpressaoDialogData } from './components/vd-impressao-dialog/vd-impressao-dialog.component';
import { VdCompartilharDialogComponent, VdCompartilharDialogData, VdCompartilharDialogResult } from './components/vd-compartilhar-dialog/vd-compartilhar-dialog.component';
import { bloqueioDeOrcamentoVencido, detectarBloqueios, motivosDaLiberacao } from './components/vd-liberacao-dialog/venda-liberacao.regras';
import { LiberacaoPresencialStruct } from '../../../shared/services/structs/vendas/liberacao-presencial.struct';
import { VdAdicionaisComponent } from './components/vd-adicionais/vd-adicionais.component';
import { VdItensComponent } from './components/vd-itens/vd-itens.component';
import { achatarItemParaRequest, montarItemDoServidor } from './components/vd-itens/venda-item.mapa';
import { VdTotaisComponent } from './components/vd-totais/vd-totais.component';
import { calcularTotaisDoDocumento, calcularTotalDoDocumento } from './components/vd-totais/venda-totais.calculo';
import { VdPagamentoComponent } from './components/vd-pagamento/vd-pagamento.component';
import { validarPagamento } from './components/vd-pagamento/venda-pagamento.validacao';
import { mensagemDoMultiplo, quantidadeEhMultipla, regraDoMultiplo } from './components/vd-itens/venda-item.multiplo';
import {
    achatarRateioParaRequest,
    achatarRecebimentoParaRequest,
    montarRateioDoServidor,
    montarRecebimentoDoServidor,
    paraDataDeCalendario,
} from './components/vd-pagamento/venda-pagamento.mapa';
import { DestinoDeEtapa, VdDadosComponent } from './components/vd-dados/vd-dados.component';
import { VdTransporteComponent } from './components/vd-transporte/vd-transporte.component';
import { VdAssinaturaComponent } from './components/vd-assinatura/vd-assinatura.component';
import { VdIntroducaoComponent } from './components/vd-introducao/vd-introducao.component';
import { VdTributosComponent } from './components/vd-tributos/vd-tributos.component';
import { VdAnexosComponent } from './components/vd-anexos/vd-anexos.component';
import { VdHistoricoComponent } from './components/vd-historico/vd-historico.component';
import {
    PvHistoricoDialogComponent,
    PvHistoricoDialogData,
} from '../processo-venda/dialogs/pv-historico-dialog/pv-historico-dialog.component';

/** Chave da etapa "Modelo" no catálogo do Kanban (etapa_kanban.chave). */
const CHAVE_ETAPA_MODELO = 'Modelo';

/** As seções da tela, com os ids do HTML e do menu lateral. */
type SecaoDocumento =
    | 'dados'
    | 'introducao'
    | 'itens'
    | 'tributos'
    | 'totais'
    | 'pagamento'
    | 'transporte'
    | 'assinatura'
    | 'adicionais'
    | 'anexos'
    | 'historico';

/** Pendência de tela que impede o salvar (§15): o que falta e em que seção se resolve. */
interface PendenciaDoSalvar {
    secao: SecaoDocumento;
    titulo: string;
    mensagem: string;
}

/**
 * Documento Comercial — a tela compartilhada do Orcamento e do Pedido de Venda.
 *
 * UMA tela para os dois: o que muda entre eles e quais secoes aparecem e quais campos cada
 * secao mostra, nunca o fluxo. `ehOrcamento` decide, e a story marca cada campo com [ORC],
 * [PED] ou [AMBOS].
 *
 * ESTRUTURA, no padrao do produto-cadastro: UM FormGroup aqui na raiz, com os FormArrays, e um
 * componente por secao em `components/`, recebendo `[form]` e os lookups de que precisa. E o que
 * evita o arquivo unico de 68 KB em que o board acabou.
 *
 * CARREGAR e SALVAR sao um par: tudo que o GET devolve em coleção (itens, recebimentos, parcelas,
 * rateio) precisa ser MONTADO no form — `patchValue` nao cria controle de FormArray — e tudo que o
 * form guarda aninhado precisa ser ACHATADO no request. Cada colecao tem o seu mapa
 * (`venda-item.mapa.ts`, `venda-pagamento.mapa.ts`), usado nas duas direcoes.
 */
@Component({
    selector: 'app-venda-documento',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatDividerModule,
        MatTooltipModule,
        HeaderComponent,
        FooterActionComponent,
        LoadingBoxComponent,
        AlertCardComponent,
        PageScrollSpyComponent,
        VdIntroducaoComponent,
        VdAssinaturaComponent,
        VdAdicionaisComponent,
        VdAnexosComponent,
        VdDadosComponent,
        VdTransporteComponent,
        VdItensComponent,
        VdTotaisComponent,
        VdTributosComponent,
        VdPagamentoComponent,
        VdHistoricoComponent,
    ],
    templateUrl: './venda-documento.component.html',
    styleUrl: './venda-documento.component.css',
})
export class VendaDocumentoComponent implements OnInit, OnDestroy {
    public readonly moduloEnum = ModuloEnum.Vendas;
    public readonly funcionalidadeEnum = FuncionalidadeEnum.Vendas_ProcessoVenda;
    public readonly tipoDocumentoEnum = VendaTipoDocumentoEnum;

    public form!: FormGroup;
    public isLoading: boolean = false;
    public isSalvando: boolean = false;

    /** Null enquanto o documento e novo — e o que faz o titulo mostrar "(novo)". */
    public idVenda: number | null = null;

    /**
     * Clonar (task 148656): o documento de ORIGEM, quando a tela abriu pela rota de novo com
     * `?origem=`. A tela nasce como documento novo com o conteúdo dele; só o texto da ocorrência de
     * Criação guarda a procedência (`idVendaOrigem` no request).
     */
    public idVendaOrigem: number | null = null;
    public rotuloOrigem: string | null = null;

    /**
     * Ações do cadastro de Pessoa (task 150696): a pessoa de `?idPessoaCliente=`, aplicada no
     * documento novo depois das configurações. `isOrigemPessoa` liga o aviso de origem.
     */
    private idPessoaClienteDaUrl: number | null = null;
    public isOrigemPessoa: boolean = false;

    /**
     * Criar orçamento ou pedido pela ficha do CRM (task 150708): a oportunidade de `?idOportunidade=`, só no
     * documento novo. Preenche cliente (travado), vendedor e "Aos cuidados de", vai no `idOportunidade` do
     * POST e faz salvar e voltar retornarem para a ficha.
     */
    private idOportunidadeDaUrl: number | null = null;
    /** Código e título da oportunidade de origem, para o aviso do topo. Null até carregar. */
    public oportunidadeOrigem: { codigo: string; titulo: string } | null = null;

    /** Pedido do menu Ações para o `vd-dados` abrir o campo Etapa — muda a cada clique. */
    public pedidoAbrirEtapa = 0;

    /** Pedido para a seção Histórico buscar de novo — muda quando o "Ver histórico" do topo fecha. */
    public pedidoRecarregarHistorico = 0;
    public idVendaTipoDocumento: number = VendaTipoDocumentoEnum.Orcamento;

    /** O documento como veio do servidor. Fonte das secoes ainda nao ligadas ao form. */
    public documento: VendaDetalheStruct | null = null;

    /** T03: credencial do supervisor coletada ao remover um item gravado (vd-itens); vale para o salvar seguinte. */
    public credencialRemocao: VdLiberacaoDialogResult | null = null;

    /**
     * O bloco INTEIRO de vendas gerais: alem da configuracao da empresa, ele traz o teto de
     * desconto por perfil e a lista de perfis que podem alterar preco (§6.5 e §6.7).
     */
    public vendasGeral: ConfigVendasGeralStruct | null = null;

    public configVendasGeral: ConfigVendasGeral | null = null;
    public configOrcamento: ConfigOrcamento | null = null;
    public configPedidoVenda: ConfigPedidoVenda | null = null;

    /** Impostos que a folha impressa mostra por item (TipoImpostoImpressaoEnum), um conjunto por processo. */
    public listImpostoImpressaoOrcamento: number[] = [];
    public listImpostoImpressaoPedido: number[] = [];
    public tags: TagVenda[] = [];

    /** Tag "1ª Venda": só quando a automação está ligada e a tag existe na lista da empresa. */
    private idTagPrimeiraVenda: number | null = null;

    /** A tela pôs a "1ª Venda" neste documento: só essa a tela tira. A que veio gravada ou o usuário pôs, fica. */
    private isTagPrimeiraVendaPelaTela: boolean = false;

    /**
     * O bloco de FINANCAS do agregado — formas, condicoes, intermediadores, contas, categorias,
     * centros de custo e rateios configurados. E o que a secao de Pagamento (§8) precisa para
     * montar cada recebimento pela NATUREZA da forma, e o que o `salvar()` usa para validar.
     */
    public financas: ConfigFinancasStruct | null = null;

    /** Formas de envio ativas da empresa, com os fretes de cada uma (agregado, §3.1). */
    public formasEnvio: FormaEnvioSelectStruct[] = [];

    /** Configuracao do Kanban: sem expedicao, "Enviar para expedicao" nao faz sentido no pedido. */
    public trabalhaComExpedicao: boolean = true;

    /** Depositos ativos da empresa (`Produto/Lookup/getLookup`, o mesmo do cadastro de produto). */
    public localizacoesEstoque: LocalizacaoEstoque[] = [];

    /** Já filtrados pelo escopo de visualização do usuário — a lista vem do agregado. */
    public vendedores: VendedorVendaStruct[] = [];

    /** Quem é o usuário logado em vendas. Diferente da lista acima, que diz o que ele pode ver. */
    public escopo: VendaEscopoStruct | null = null;

    /**
     * Catálogo do kanban, do agregado (cacheado por empresa): as CORES pintam a etapa das
     * vendas no modal do cliente e o badge do cabeçalho; os PROCESSOS e ícones dizem em que
     * etapa o documento está — e se essa etapa o encerra (§1.4).
     */
    public coresKanban: KanbanCor[] = [];
    private iconesKanban: KanbanIcone[] = [];
    private processosKanban: KanbanProcessoStruct[] = [];
    private motivosKanban: MotivoTransicao[] = [];

    /**
     * Etapa (§9): os destinos que o campo oferece vêm do SERVIDOR no GET do documento
     * (`idsEtapaDestinoPermitida`, a mesma régua do board e do POST /etapa); aqui só se
     * resolve o visual de cada um no catálogo do Kanban.
     */
    private idsEtapaDestinoPermitida: number[] = [];
    public destinosDeEtapa: DestinoDeEtapa[] = [];
    public isMovendoEtapa: boolean = false;

    /** A etapa atual do documento CARREGADO, resolvida no catálogo. Nula no documento novo. */
    public etapaAtual: EtapaKanbanStruct | null = null;
    public corEtapa: PvCorVm | null = null;
    public iconeEtapa: string | null = null;

    /**
     * As mascaras da TELA, instanciadas uma vez so.
     *
     * Ficam aqui, e nao em cada secao, porque nao sao dos itens: os totais, o pagamento e as
     * parcelas usam as mesmas. E cada `getDynamic...Mask()` le o usuario logado —
     * instanciar por componente repetiria essa leitura a cada secao da tela.
     *
     * `allowNegative: false` em todas, pela convencao ja usada em contas a pagar e receber:
     * quantidade, preco e total de um documento de venda nao sao negativos, e deixar o sinal
     * passar no campo so adia o erro para o salvamento. As DINAMICAS recebem isso por parametro;
     * `getIntegerMask` e `getPercentMask` ainda nao o expoem, entao so essas duas levam override.
     */
    public readonly mascaraPreco: Partial<NgxCurrencyConfig> =
        this.maskService.getDynamicCurrencyMask(null, false);

    /**
     * Dinheiro do DOCUMENTO — desconto, frete, despesas, e depois os valores do pagamento.
     *
     * `getCurrencyMask()` e nao a dinamica: duas casas fixas, e nao as casas de PRECO da empresa.
     * A configuracao de preco rege o preco do PRODUTO (lista, unitario), que pode precisar de
     * fracao de centavo para caber a unidade; frete e despesa nao sao preco de nada — sao valores
     * a cobrar, e centavo e a menor parte que existe. Mesma linha que o total do item ja segue.
     */
    public readonly mascaraDinheiro: Partial<NgxCurrencyConfig> = {
        ...this.maskService.getCurrencyMask(),
        allowNegative: false,
    };

    public readonly mascaraQuantidade: Partial<NgxCurrencyConfig> =
        this.maskService.getDynamicDecimalMask(null, false);

    public readonly mascaraInteira: Partial<NgxCurrencyConfig> = {
        ...this.maskService.getIntegerMask(),
        allowNegative: false,
    };

    /**
     * Percentual nao tem configuracao de casas decimais propria — sempre 2, como o desconto do
     * documento. Preco e quantidade seguem a empresa.
     */
    public readonly mascaraPercentual: Partial<NgxCurrencyConfig> = {
        ...this.maskService.getPercentMask(2),
        allowNegative: false,
    };

    /**
     * Os itens do documento GRAVADO, que descem para a seção de Itens por binding.
     *
     * Por `@Input`, e não por `ViewChild`: a seção vive dentro do `@else` do `isLoading`, então
     * no instante em que a resposta chega o componente ainda não existe e a query não resolveu.
     * O binding não tem esse problema — o Angular entrega assim que a seção é criada.
     *
     * Quem monta cada linha é o `vd-itens`, e não este shell: linha carregada tem de nascer
     * igual à lançada, que é a mesma razão de `preencherDoProduto` ser uma função só.
     */
    public itensDoDocumento: Record<string, unknown>[] | null = null;

    /** Preenchido ao carregar um documento que já tem cliente. */
    public clienteSelecionado: ClienteVendaSelectStruct | null = null;

    /**
     * O financeiro do cliente escolhido (limite, em aberto, vencido). Sobe da seção 1 porque é
     * aqui que o documento é gravado — e é no gravar que o atraso pode barrar.
     */
    public clienteFinanceiro: ClienteDetalheStruct | null = null;

    /** O usuário acabou de escolher um cliente e "Aos cuidados de" pode ser preenchido: o 1º contato dele entra quando chegar. */
    private aguardaContatoDoCliente: boolean = false;

    /** O que o preenchimento automático colocou em "Aos cuidados de": trocar de cliente substitui isso, nunca o digitado à mão. */
    private aosCuidadosDeAutomatico: string | null = null;

    /** Contador do "Recalcular preços" (§4.1): cada clique muda o valor, e a mudança é o sinal para a seção de itens. */
    public pedidoRecalcularPrecos: number = 0;

    /**
     * Anexos fora do form, de proposito: sao arquivo, e um FormControl com base64 dentro faria
     * cada valueChanges do documento carregar megabytes.
     */
    public anexos: VendaAnexoSalvarStruct[] = [];

    /**
     * Documento em etapa de ENCERRAMENTO (§1.4): tudo desabilitado e sem botao Salvar.
     *
     * Quem diz quais etapas encerram e a CONFIGURACAO do Kanban — `isFinal` e editavel pela
     * empresa —, mais o "Modelo", que e template e nunca se edita como documento. Nao ha lista
     * fixa aqui: e a decisao do Pedro (a etapa e configuravel).
     */
    public isSomenteLeitura: boolean = false;

    /** O `carregar` desabilitou o form inteiro por somente-leitura; o próximo `carregar` editável o devolve. */
    private isFormDesabilitadoPorLeitura: boolean = false;

    /**
     * Seções recolhidas. Todas recolhem; a configuração do pedido decide só como Transporte e
     * Dados adicionais abrem. O conteúdo fica no DOM (`[hidden]`), então recolher não perde nada.
     */
    public readonly recolhidas: Partial<Record<SecaoDocumento, boolean>> = {};

    /** O que impediu o último salvar (§15): a faixa do topo lista e leva à seção; some no salvar sem pendência. */
    public pendenciasDoSalvar: PendenciaDoSalvar[] = [];

    /** A barra de seções, para rolar até a seção da pendência (ela também expande a seção recolhida). */
    @ViewChild(PageScrollSpyComponent) private barraSecoes?: PageScrollSpyComponent;

    /**
     * Etapa que encerra o documento (`isFinal` ou Modelo). Prevalece sobre a leitura por integração e é
     * o que esconde "Alterar etapa": documento só com lançamentos ainda muda de etapa.
     */
    public isEncerrado: boolean = false;

    /**
     * Documento com integração (financeiro, estoque, NF-e...) também abre em leitura, como o PUT no
     * servidor recusa. Null sem integração; senão, o texto da faixa.
     */
    public motivoLeituraIntegracao: string | null = null;

    /** Nome da lista de preço escolhida, vindo do lookup da seção Dados. Só informativo no salvar. */
    public nomeListaPrecoSelecionada: string | null = null;

    /**
     * Nome de cada kit do documento CARREGADO (idProdutoKit -> descrição). Só informativo no salvar: o
     * kit removido já saiu da grade, e a ocorrência de edição ainda precisa do nome dele.
     */
    private nomesKitCarregados: Record<number, string> = {};

    private readonly destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private dialog: MatDialog,
        private alertService: AlertService,
        private vendaDocumentoService: VendaDocumentoService,
        private maskService: MaskService,
        private authService: AuthService,
        private produtoLookupService: ProdutoLookupService,
        private processoVendaService: ProcessoVendaService,
        private motorFiscalLookupService: MotorFiscalLookupService,
        private pessoaService: PessoaService,
        private pessoaLookupService: PessoaLookupService,
        private oportunidadeService: OportunidadeService,
        private themeService: ThemeService,
        private elementRef: ElementRef<HTMLElement>,
    ) {}

    private observadorBarraSecoes?: ResizeObserver;

    /**
     * Altura da barra de seções (que quebra em mais linhas conforme a largura) publicada em `--vd-altura-secoes`, como o
     * alert-banner faz com a dele: a linha de abas e ações da seção de itens gruda logo abaixo da barra. A barra vive
     * dentro do `@else` do carregamento, por isso o setter.
     */
    @ViewChild('barraSecoes', { read: ElementRef }) set barraSecoesRef(ref: ElementRef<HTMLElement> | undefined) {
        this.observadorBarraSecoes?.disconnect();

        const barra = ref?.nativeElement.querySelector<HTMLElement>('.scroll-spy-container');
        if (!barra) return;

        const publicar = () => this.elementRef.nativeElement.style.setProperty('--vd-altura-secoes', `${barra.offsetHeight}px`);
        publicar();
        this.observadorBarraSecoes = new ResizeObserver(publicar);
        this.observadorBarraSecoes.observe(barra);
    }

    public ngOnDestroy(): void {
        this.observadorBarraSecoes?.disconnect();
        this.destroy$.next();
        this.destroy$.complete();
    }

    public ngOnInit(): void {
        this.montarForm();
        this.ligarObrigatoriedadeDoEnderecoDeEntrega();

        // §15: a faixa de pendências acompanha o que o usuário resolve, enquanto estiver na tela; some sozinha quando
        // não sobra pendência. Só recalcula com a faixa visível, para não pesar cada tecla do documento.
        this.form.valueChanges
            .pipe(debounceTime(400), takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.pendenciasDoSalvar.length === 0) return;

                this.pendenciasDoSalvar = this.pendenciasDoSalvarAtuais(this.itens.controls as FormGroup[]);
            });

        // A cor da etapa (cabeçalho, campo Etapa e destinos) é resolvida por tema em `resolverCor` e vai como
        // style inline, que o CSS não troca sozinho: a troca de tema re-resolve, como o board faz.
        this.themeService.themeChanged$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.resolverEtapa(this.etapaAtual?.idEtapaKanban));

        const idVenda = this.route.snapshot.paramMap.get('idVenda');
        const idTipo = this.route.snapshot.queryParamMap.get('tipo');

        // Documento novo: o tipo vem da URL (?tipo=), porque e ele que decide a tela inteira — quais
        // secoes existem, quais campos aparecem e em que processo o documento nasce.
        this.idVendaTipoDocumento = idVenda ? this.idVendaTipoDocumento : Number(idTipo) || VendaTipoDocumentoEnum.Orcamento;
        this.form.patchValue({ idVendaTipoDocumento: this.idVendaTipoDocumento });

        if (idVenda) this.idVenda = Number(idVenda);

        const origem = Number(this.route.snapshot.queryParamMap.get('origem'));

        if (!idVenda && origem > 0) this.idVendaOrigem = origem;

        // Ficha do CRM (150708): documento novo ligado à oportunidade. O cliente é o dela, então
        // `idPessoaCliente`, se vier junto, é ignorado.
        const idOportunidade = Number(this.route.snapshot.queryParamMap.get('idOportunidade'));

        if (!idVenda && this.idVendaOrigem == null && Number.isInteger(idOportunidade) && idOportunidade > 0) {
            this.idOportunidadeDaUrl = idOportunidade;

            if (!this.podeInserir) {
                this.alertService.show(
                    'Aviso', 'Você não tem permissão para incluir orçamentos e pedidos de venda.', AlertType.warning);
                this.voltarParaOportunidade(true);
                return;
            }
        }

        // Ações do cadastro de Pessoa (150696): documento novo que já nasce com o cliente.
        const idPessoaCliente = Number(this.route.snapshot.queryParamMap.get('idPessoaCliente'));

        if (!idVenda && this.idVendaOrigem == null && this.idOportunidadeDaUrl == null
            && Number.isInteger(idPessoaCliente) && idPessoaCliente > 0) {
            // PA-05: a permissão é conferida aqui, no destino; sem Inserir, volta para a lista.
            if (!this.podeInserir) {
                this.alertService.show(
                    'Aviso', 'Você não tem permissão para incluir orçamentos e pedidos de venda.', AlertType.warning);
                // replaceUrl: o Voltar do navegador não pode cair de novo nesta URL.
                this.router.navigate(['/vendas/processo-venda'], { replaceUrl: true });
                return;
            }

            this.idPessoaClienteDaUrl = idPessoaCliente;
        }

        this.carregarConfiguracoes();
    }

    // ================================================================== guarda de saida

    /**
     * Fechar ou recarregar o navegador com alteracao pendente abre o alerta nativo (§9.1 e task
     * 148617). Nao ha rascunho automatico: sair e perder tudo.
     */
    @HostListener('window:beforeunload', ['$event'])
    public avisarAoFecharNavegador(evento: BeforeUnloadEvent): void {
        if (!this.temAlteracoesPendentes) return;

        evento.preventDefault();
        // Navegadores antigos so mostram o alerta com returnValue preenchido.
        evento.returnValue = '';
    }

    /**
     * Alteracao pendente = o usuario mexeu no form (o `patchValue` do carregamento nao suja o
     * form) ou anexou arquivo ainda nao gravado. Documento em leitura nunca tem pendencia.
     */
    public get temAlteracoesPendentes(): boolean {
        if (this.isSomenteLeitura || this.isSalvando) return false;

        return this.form?.dirty === true || this.anexos.some((a) => !a.idVendaAnexo);
    }

    /**
     * As configuracoes vem ANTES do documento, e o documento so e carregado depois delas: e a
     * configuracao que diz quais campos sao editaveis e que textos um documento novo ja traz.
     * Carregar na ordem inversa faria o padrao da configuracao sobrescrever o que esta gravado.
     */
    private carregarConfiguracoes(): void {
        this.isLoading = true;

        this.vendaDocumentoService.getConfiguracoes().subscribe({
            next: (res) => {
                if (res.isError) {
                    this.isLoading = false;
                    if (this.idPessoaClienteDaUrl != null) this.limparPessoaDaUrl();
                    this.alertService.show('Erro', res.errorDescription ?? 'Não foi possível carregar as configurações.', AlertType.error);
                    return;
                }

                this.vendasGeral = res.vendasGeral ?? null;
                this.configVendasGeral = res.vendasGeral?.configVendasGeral ?? null;
                this.configOrcamento = res.orcamento?.configOrcamento ?? null;
                this.configPedidoVenda = res.pedidoVenda?.configPedidoVenda ?? null;
                this.listImpostoImpressaoOrcamento = res.orcamento?.listImpostoImpressao ?? [];
                this.listImpostoImpressaoPedido = res.pedidoVenda?.listImpostoImpressao ?? [];
                this.tags = res.tagsVenda ?? [];

                const idTagPrimeiraVenda = res.automacaoVenda?.idTagVendaPrimeiraVenda ?? null;
                this.idTagPrimeiraVenda = res.automacaoVenda?.config?.isAtivoPrimeiraVenda
                    && this.tags.some((t) => t.idTagVenda === idTagPrimeiraVenda)
                    ? idTagPrimeiraVenda
                    : null;
                this.financas = res.financas ?? null;
                this.vendedores = res.vendedores ?? [];
                this.escopo = res.escopo ?? null;
                this.coresKanban = res.kanban?.cores ?? [];
                this.iconesKanban = res.kanban?.icones ?? [];
                this.processosKanban = res.kanban?.processos ?? [];
                this.motivosKanban = res.kanban?.motivos ?? [];
                this.formasEnvio = res.formasEnvio ?? [];
                this.trabalhaComExpedicao = res.kanban?.isTrabalhaComExpedicao ?? true;

                this.carregarLookupDeProduto();

                if (this.idVenda != null) {
                    this.carregar();
                    return;
                }

                if (this.idVendaOrigem != null) {
                    this.carregarOrigemDoClone(this.idVendaOrigem);
                    return;
                }

                this.aplicarPadroesDaConfiguracao();
                this.aplicarValidadoresDaConfiguracao();
                this.aplicarSecoesRecolhidasDaConfiguracao();

                // Depois dos padrões: o vendedor da pessoa só substitui o do usuário quando a tela o oferece.
                if (this.idPessoaClienteDaUrl != null) {
                    this.iniciarDaPessoa(this.idPessoaClienteDaUrl);
                    return;
                }

                // Idem para a oportunidade: o vendedor responsável dela substitui o padrão.
                if (this.idOportunidadeDaUrl != null) {
                    this.iniciarDaOportunidade(this.idOportunidadeDaUrl);
                    return;
                }

                this.isLoading = false;
            },
            error: (err) => {
                this.isLoading = false;
                if (this.idPessoaClienteDaUrl != null) this.limparPessoaDaUrl();
                this.alertService.show('Aviso', err.message, AlertType.warning);
            },
        });
    }

    /**
     * Endereco de entrega proprio e tudo ou nada: marcado o toggle, os campos que formam um
     * endereco passam a ser obrigatorios. Entregar num lugar pela metade nao e entregar.
     *
     * Complemento fica de fora — nem todo endereco tem. E, ao desmarcar, os validators saem
     * junto: campo escondido invalido travaria o salvamento sem a tela poder explicar por que.
     */
    private ligarObrigatoriedadeDoEnderecoDeEntrega(): void {
        const campos = [
            'entregaCep',
            'entregaLogradouro',
            'entregaNumero',
            'entregaBairro',
            'entregaUf',
            'entregaIdMunicipio',
        ];

        const aplicar = (exigido: boolean): void => {
            for (const nome of campos) this.exigir(nome, exigido);
        };

        aplicar(this.form.get('isEntregaEnderecoDiferente')?.value === true);

        this.form
            .get('isEntregaEnderecoDiferente')
            ?.valueChanges.subscribe((marcado: boolean) => aplicar(marcado === true));
    }

    /**
     * Os obrigatorios que dependem da CONFIGURACAO ou do TIPO do documento (§4.2, §4.3, §1.3).
     *
     * O servidor ja barra todos eles; aqui e o retorno imediato no campo, para o usuario nao
     * descobrir a falta so depois de clicar em Salvar. Roda depois das configuracoes e de novo
     * depois do carregamento, porque o tipo do documento pode mudar com o GET.
     */
    private aplicarValidadoresDaConfiguracao(): void {
        this.exigir('idVendedor', this.vendedorObrigatorio);
        this.exigir('idNatOp', this.calcularImpostos);
        this.exigir('numero', this.numeracaoManual);
        this.exigir('validadeDias', this.ehOrcamento);
    }

    private exigir(nome: string, exigido: boolean): void {
        const controle = this.form.get(nome);

        if (!controle) return;

        if (exigido) {
            controle.addValidators(Validators.required);
        } else {
            controle.removeValidators(Validators.required);
        }

        controle.updateValueAndValidity({ emitEvent: false });
    }

    /**
     * O que um documento NOVO ja traz preenchido pela configuracao. So no novo: num documento
     * existente, o que vale e o que foi gravado, mesmo que a configuracao tenha mudado depois.
     */
    private aplicarPadroesDaConfiguracao(): void {
        const hoje = new Date();
        this.form.patchValue({ dataEmissao: new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()) });

        // Vendedor que abre um documento novo ja e o vendedor dele: digitar o proprio nome toda
        // vez seria trabalho para confirmar o obvio. So no NOVO — abrindo um existente vale quem
        // esta gravado, ainda que seja outra pessoa. Quem nao e vendedor escolhe na lista.
        if (this.escopo?.isVendedor && this.escopo.idVendedor != null) {
            this.form.patchValue({ idVendedor: this.escopo.idVendedor });
        }

        this.aplicarNaturezaPadraoVenda();

        if (this.ehOrcamento) {
            if (!this.configOrcamento) return;

            this.form.patchValue({
                introducaoProposta: this.configOrcamento.textoIntroducao,
                assinaturaSaudacao: this.configOrcamento.assinaturaSaudacao,
                assinaturaResponsavel: this.configOrcamento.assinaturaResponsavel,
                observacoes: this.configOrcamento.observacaoPadrao,
                validadeDias: this.configOrcamento.isOrcamentoExpiraAutomatico
                    ? this.configOrcamento.prazoValidadeDias
                    : null,
            });

            return;
        }

        // PEDIDO: a observacao padrao e a quantidade de volumes vem da configuracao do processo
        // (story do Pedido §5.4 e §6), e "Enviar para expedicao" nasce ligado - e o DEFAULT da
        // coluna, que o request (bool nao anulavel) nunca deixaria valer se o form mandasse false.
        this.form.patchValue({
            observacoes: this.configPedidoVenda?.observacaoPadrao || null,
            volume: { quantidade: this.configPedidoVenda?.quantidadeVolumesPadrao ?? null },
            isEnviarExpedicao: true,
        });
    }

    /**
     * Documento novo nasce com a natureza padrao de venda do cadastro fiscal. So com o calculo de
     * impostos ligado: desligado, o campo nem aparece e o documento segue sem natureza, como sempre.
     * A lista chega depois de a tela abrir; se o usuario ja escolheu uma natureza, ela fica.
     */
    private aplicarNaturezaPadraoVenda(): void {
        if (!this.calcularImpostos) return;

        this.motorFiscalLookupService.getNaturezaOperacaoDocumentoLookup(TipoOperacaoEnum.Saida).subscribe({
            next: (res) => {
                const naturezaPadraoVenda = (res?.lista ?? []).find((n) => n.tiposPadrao.includes(TipoPadraoNaturezaEnum.Venda));

                if (naturezaPadraoVenda && !this.form.get('idNatOp')?.value) {
                    this.form.patchValue({ idNatOp: naturezaPadraoVenda.idNaturezaOperacao });
                }
            },
            error: () => {},
        });
    }

    /**
     * Automação "1ª Venda": cliente sem orçamento nem pedido anterior (o histórico já ignora excluídos e modelos)
     * recebe a tag. Vale ao escolher ou trocar o cliente, em documento novo ou gravado. Histórico nulo (carregando,
     * sem cliente ou falha na consulta) não aplica. Se o cliente mudar para um com histórico, a tela tira só a tag
     * que ela mesma pôs. O servidor não reaplica: o usuário pode tirar a tag.
     */
    public aplicarTagPrimeiraVenda(historico: HistoricoClienteStruct | null): void {
        const idTag = this.idTagPrimeiraVenda;
        if (idTag == null || this.isSomenteLeitura) return;

        const control = this.form.get('idsTagVenda');
        const ids: number[] = control?.value ?? [];
        const ehPrimeiraVenda = historico != null && historico.vendas.length === 0;

        if (ehPrimeiraVenda && !ids.includes(idTag)) {
            control?.setValue([...ids, idTag]);
            this.isTagPrimeiraVendaPelaTela = true;
        } else if (!ehPrimeiraVenda && this.isTagPrimeiraVendaPelaTela && ids.includes(idTag)) {
            control?.setValue(ids.filter((id) => id !== idTag));
            this.isTagPrimeiraVendaPelaTela = false;
        }
    }

    /** §6.1: as abas fiscais dependem da configuracao DO PROCESSO, como o campo Natureza. */
    public get calcularImpostos(): boolean {
        return this.ehOrcamento
            ? this.configOrcamento?.calcularImpostos === true
            : this.configPedidoVenda?.isCalcularImpostos === true;
    }

    /** §4.3: a configuracao e de CADA processo, e nao ha heranca entre eles na conversao. */
    public get vendedorObrigatorio(): boolean {
        return this.ehOrcamento
            ? this.configOrcamento?.vendedorObrigatorio === true
            : this.configPedidoVenda?.isVendedorObrigatorio === true;
    }

    /**
     * Transporte (§3.1) existe nos dois documentos; a configuracao de exibicao e so do PEDIDO
     * (decisao D12: vale para a secao inteira). No orcamento a secao e sempre visivel e aberta.
     */
    public get exibeTransporte(): boolean {
        if (this.ehOrcamento) return true;

        return this.configPedidoVenda?.idTipoExibicaoTransportador !== TipoExibicaoSecaoPedidoEnum.Nao;
    }

    private get transporteNasceRecolhida(): boolean {
        return !this.ehOrcamento
            && this.configPedidoVenda?.idTipoExibicaoTransportador === TipoExibicaoSecaoPedidoEnum.ExibirRecolhido;
    }

    /** Dados adicionais: mesma regra, pela configuracao "Dados adicionais" do pedido. */
    public get exibeAdicionais(): boolean {
        if (this.ehOrcamento) return true;

        return this.configPedidoVenda?.idTipoExibicaoDadosAdicionais !== TipoExibicaoSecaoPedidoEnum.Nao;
    }

    private get adicionaisNasceRecolhida(): boolean {
        return !this.ehOrcamento
            && this.configPedidoVenda?.idTipoExibicaoDadosAdicionais === TipoExibicaoSecaoPedidoEnum.ExibirRecolhido;
    }

    /**
     * Como Transporte e Dados adicionais abrem ("Exibir recolhido" da configuracao do pedido). Roda
     * quando o tipo do documento e conhecido: depois das configuracoes no novo e depois do GET.
     */
    private aplicarSecoesRecolhidasDaConfiguracao(): void {
        this.recolhidas.transporte = this.transporteNasceRecolhida;
        this.recolhidas.adicionais = this.adicionaisNasceRecolhida;
    }

    public isRecolhida(secao: SecaoDocumento): boolean {
        return this.recolhidas[secao] === true;
    }

    public alternarSecao(secao: SecaoDocumento): void {
        this.recolhidas[secao] = !this.isRecolhida(secao);
    }

    /** Clicar na barra de navegação expande a seção antes de rolar até ela. */
    public aoSelecionarSecao(id: string): void {
        this.recolhidas[id as SecaoDocumento] = false;
    }

    /**
     * Do cadastro de produto, só o que o documento usa (Lookup/lookupsVenda): depósitos e listas de preço.
     *
     * O depósito é do ITEM: a seção de Itens aplica o padrão (`isPadrao`; a API garante que existe um) na linha
     * nova e o modal do item oferece a lista para trocar.
     *
     * A lista de preço padrão (também garantida pela API) entra no documento NOVO, orçamento ou pedido (PO, 26/10),
     * como a natureza padrão de venda: não no gravado nem no clone, e só se o usuário ainda não escolheu uma.
     */
    private carregarLookupDeProduto(): void {
        this.produtoLookupService.getLookupsVenda().subscribe({
            next: (res) => {
                if (res.isError) return;

                this.localizacoesEstoque = (res.listaLocalizacaoEstoque ?? []).filter((l) => l.isAtivo !== false);

                const listaPadrao = (res.listaPreco ?? []).find((l) => l.listaPreco?.isPadrao)?.listaPreco;
                const ehNovo = this.idVenda == null && this.idVendaOrigem == null;

                if (ehNovo && listaPadrao && this.form.get('idListaPreco')?.value == null) {
                    this.form.patchValue({ idListaPreco: listaPadrao.idListaPreco });
                    this.nomeListaPrecoSelecionada = listaPadrao.descricao;
                }
            },
            error: () => {
                this.localizacoesEstoque = [];
            },
        });
    }

    /** §1.3: com numeracao MANUAL o numero e informado na criacao — e passa a ser obrigatorio. */
    public get numeracaoManual(): boolean {
        return this.configVendasGeral?.idTipoNumeracaoDocumento === TipoNumeracaoDocumentoEnum.Manual;
    }

    /**
     * §6.2.2.3: com isto ligado, o preço do item é o MENOR entre a lista do cabeçalho e as
     * promocionais vigentes. O default da configuração é ligado, e é o que vale enquanto ela não
     * chegou — desligado, o vendedor deixaria de ver a promoção que a empresa está fazendo.
     */
    public get priorizarMenorPreco(): boolean {
        return this.configVendasGeral?.isPriorizarMenorPreco !== false;
    }

    /** A lista escolhida no cabeçalho, que a seção de Itens usa como base da resolução. */
    public get idListaCabecalho(): number | null {
        return this.form.get('idListaPreco')?.value ?? null;
    }

    /** A natureza do documento, com que o item novo nasce preenchido (§6.4). */
    public get idNatOpCabecalho(): number | null {
        return this.form.get('idNatOp')?.value ?? null;
    }

    /** §6.2: sem isto, preco de lista e desconto nao existem na grade nem no modal do item. */
    public get exibeDescontoPorItem(): boolean {
        return this.configVendasGeral?.isExibirPrecoListaDescontoItens === true;
    }

    /**
     * O vendedor escolhido no cabecalho, resolvido na lista que o agregado ja trouxe.
     *
     * A regra de comissao vem junto com ele (tipo, percentual fixo e faixas), entao trocar o
     * vendedor nao custa requisicao nenhuma.
     */
    public get vendedorSelecionado(): VendedorVendaStruct | null {
        const idVendedor = this.form?.get('idVendedor')?.value;

        if (!idVendedor) return null;

        return this.vendedores.find((v) => v.idVendedor === idVendedor) ?? null;
    }

    /**
     * §6.5 — o teto de desconto do usuario, em percentual. Nulo = sem teto.
     *
     * O teto da empresa (`descontoMaximoItens`) vale para quem nao e master — o master tem
     * alcada total e nao pede liberacao a si mesmo (Pedro, 15/10, revendo a decisao de 23/09):
     * o `salvar()` pula a deteccao de bloqueios para ele e a folha nao confere o teto. A
     * lista por perfil e a EXCECAO: "Sem excecoes cadastradas, todos os perfis usam o desconto
     * maximo dos itens". Tendo o usuario mais de um perfil com excecao, vale o maior — o perfil
     * mais permissivo e o que ele de fato possui.
     *
     * `100` na configuracao significa "nao limitar" ("Para nao limitar, informe o valor 100"),
     * e vira nulo aqui para a tela nao avisar sobre um teto que nao existe.
     */
    public get tetoDesconto(): number | null {
        const excecoes = (this.vendasGeral?.listDescontoPerfil ?? [])
            .filter((d) => (this.authService.getUserInfos()?.listaIdPerfil ?? []).includes(d.idPerfil))
            .map((d) => d.descontoMaximo);

        const teto = excecoes.length
            ? Math.max(...excecoes)
            : (this.configVendasGeral?.descontoMaximoItens ?? 100);

        return teto >= 100 ? null : teto;
    }

    /**
     * O Modelo nasce de orçamento e É um orçamento (só o processo Orçamento tem a coluna Modelo):
     * abre com as seções dele - Introdução, Prazos do orçamento, Assinatura.
     */
    public get ehOrcamento(): boolean {
        return this.idVendaTipoDocumento === VendaTipoDocumentoEnum.Orcamento
            || this.idVendaTipoDocumento === VendaTipoDocumentoEnum.Modelo;
    }

    /** "Permite estoque negativo" (Configurações ▸ Vendas ▸ Gerais). */
    public get tipoValidacaoEstoqueNegativo(): number | null {
        return this.configVendasGeral?.idTipoValidacaoEstoqueNegativo ?? null;
    }

    public get titulo(): string {
        const tipo = this.nomeDoTipoDeDocumento;
        const numero = this.form?.get('numero')?.value;

        if (numero) return `${tipo} #${numero}`;

        // "(novo)" só quando o documento É novo. Abrindo um existente, o número só chega com o
        // GET — e até lá o título dizia "(novo)" para um documento que já existe, que é o tipo
        // de coisa que faz o usuário achar que perdeu o trabalho.
        return this.idVenda == null ? `${tipo} (novo)` : tipo;
    }

    /**
     * O Modelo é template (nasce de outro documento, abre em leitura) e a tela o trata com as
     * seções do pedido — mas o título diz o que ele é, senão o usuário lê "Pedido de Venda" num
     * documento que nunca vira pedido.
     */
    private get nomeDoTipoDeDocumento(): string {
        switch (this.idVendaTipoDocumento) {
            case VendaTipoDocumentoEnum.Orcamento: return 'Orçamento';
            case VendaTipoDocumentoEnum.Modelo: return 'Modelo';
            default: return 'Pedido de Venda';
        }
    }

    /** Título da faixa: o encerramento prevalece — excluir o lançamento não reabre etapa final. */
    public get tituloSomenteLeitura(): string {
        return this.motivoLeituraIntegracao && !this.isEncerrado ? 'Documento com lançamentos' : 'Documento somente leitura';
    }

    /** A faixa do topo quando o documento esta encerrado (§1.4) ou tem integração. */
    public get motivoSomenteLeitura(): string {
        if (this.motivoLeituraIntegracao && !this.isEncerrado) return this.motivoLeituraIntegracao;

        const nome = this.etapaAtual?.nome ?? 'encerramento';

        return `Este documento está em "${nome}" e não pode mais ser editado. O campo Etapa e as ações não destrutivas continuam disponíveis.`;
    }

    /**
     * A trilha de ancoras lista APENAS as secoes renderizadas (story 3). Secao escondida por
     * configuracao nao aparece aqui nem tem seus campos exigidos em validacao nenhuma.
     */
    public get sections(): ScrollSpySection[] {
        const orc = this.ehOrcamento;

        const secoes: { id: SecaoDocumento; label: string; on: boolean }[] = [
            // O CLIENTE e o endereco de entrega sao subsecoes de Dados gerais, como na NF-e de saida.
            { id: 'dados', label: 'Dados gerais', on: true },
            { id: 'introducao', label: 'Introdução', on: orc },
            { id: 'itens', label: orc ? 'Itens do orçamento' : 'Itens da venda', on: true },
            { id: 'tributos', label: 'Tributos', on: this.calcularImpostos },
            { id: 'totais', label: 'Totais', on: true },
            { id: 'pagamento', label: 'Pagamento', on: true },
            { id: 'transporte', label: 'Transporte', on: this.exibeTransporte },
            { id: 'assinatura', label: 'Assinatura', on: orc },
            { id: 'adicionais', label: 'Dados adicionais', on: this.exibeAdicionais },
            { id: 'anexos', label: 'Anexos', on: true },
            { id: 'historico', label: 'Histórico', on: this.idVenda != null },
        ];

        return secoes
            .filter((s) => s.on)
            .map(({ id, label }) => ({ id, label }));
    }

    public get itens(): FormArray {
        return this.form.get('itens') as FormArray;
    }

    /** Linhas com produto, para o badge do título da seção: a contagem fica visível mesmo com a seção recolhida. */
    public get quantidadeDeItens(): number {
        return this.itens.controls.filter((linha) => !!linha.get('dados.idProduto')?.value).length;
    }

    public get pagamentos(): FormArray {
        return this.form.get('pagamentos') as FormArray;
    }

    public get rateioCentroCusto(): FormArray {
        return this.form.get('rateioCentroCusto') as FormArray;
    }

    /** Nomes técnicos dos campos configuráveis do contas a receber marcados como obrigatórios (DF10). */
    public get camposObrigatoriosContasReceber(): string[] {
        return (this.financas?.listaCampoObrigatorioContasReceber ?? [])
            .filter((c) => c.isObrigatorio && !c.isFixo)
            .map((c) => c.nomeTecnico);
    }

    /** NSU obrigatório nas parcelas quando o contas a receber exige (só no pedido). */
    public get isNsuObrigatorio(): boolean {
        return !this.ehOrcamento && this.camposObrigatoriosContasReceber.includes(CampoContasReceberEnum.Nsu);
    }

    /** Observação da parcela obrigatória quando o contas a receber exige e o pedido não tem observações internas. */
    public get isObservacaoParcelaObrigatoria(): boolean {
        return !this.ehOrcamento && this.camposObrigatoriosContasReceber.includes(CampoContasReceberEnum.Observacoes)
            && !String(this.form.get('observacoesInternas')?.value ?? '').trim();
    }

    /**
     * `itens + frete + despesas − desconto` (§7) — UMA conta para a secao de Totais e para a
     * cobertura da secao de Pagamento, que precisam concordar até o centavo. Getter, e nao campo
     * sincronizado: mexeu no item ou no frete, o proximo ciclo de deteccao ja le o valor novo.
     */
    public get totalDoDocumento(): number {
        return calcularTotalDoDocumento(this.form);
    }

    /**
     * Σ dos recebimentos cujas formas têm "Validar limite de crédito" (task 150887) — o que a conferência de
     * crédito compara com o limite disponível do cliente. As formas são as do agregado de configurações.
     */
    public get valorSujeitoALimite(): number {
        const formas = this.financas?.listaFormaRecebimento ?? [];
        const recebimentos = ((this.form?.get('pagamentos') as FormArray | null)?.controls ?? []) as FormGroup[];

        return recebimentos
            .filter((r) => formas.find((f) => f.id === r.get('idFormaRecebimento')?.value)?.isValidaLimiteCredito === true)
            .reduce((soma, r) => soma + (Number(r.get('valor')?.value) || 0), 0);
    }

    /** T03: remover item já gravado sem liberação exige a permissão "Remover item"; o master tem alçada total. */
    public get podeRemoverItem(): boolean {
        const usuario = this.authService.getUserInfos();

        return usuario?.master === true || (usuario?.listaPermissoes?.includes(PermissaoEnum.Vendas_ProcessoVenda_RemoverItem) ?? false);
    }

    /** T03: itens gravados (`documento.itens`) que não estão mais nas linhas, como "Descrição × qtde". */
    private itensGravadosRemovidos(linhas: FormGroup[]): string[] {
        const idsNasLinhas = new Set(linhas.map((l) => Number(l.get('dados.idVendaItem')?.value) || 0));

        return (this.documento?.itens ?? [])
            .filter((item) => item.idVendaItem > 0 && !idsNasLinhas.has(item.idVendaItem))
            .map((item) => `${item.descricao || 'item'} × ${Number(item.quantidade).toLocaleString('pt-BR', { maximumFractionDigits: 4 })}`);
    }

    /** Desconto do documento em R$: a seção de itens rateia por linha (D21) para comissão e teto. */
    public get valorDescontoGlobal(): number {
        return Number(this.form?.get('valorDesconto')?.value ?? 0) || 0;
    }

    /**
     * O form raiz, agrupado pelas secoes da tela. Os validadores que dependem de configuracao
     * entram depois (`aplicarValidadoresDaConfiguracao`); aqui ficam so os fixos da story.
     */
    private montarForm(): void {
        this.form = this.fb.group({
            idVendaTipoDocumento: [this.idVendaTipoDocumento],
            // Nulo enquanto o servidor nao atribui (numeracao Sequencial); o request manda 0.
            numero: [null as number | null],
            isOrigemOrcamento: [false],

            // ---- Secao 1: dados gerais - Documento ---------------------------------------
            idVendedor: [null as number | null],
            dataEmissao: [null as Date | string | null, Validators.required],
            idListaPreco: [null as number | null],
            idNatOp: [null as number | null],
            dataProximoContato: [null as Date | string | null],
            numeroPedidoCliente: [null as string | null],
            // Intermediador da transacao (indIntermed e infIntermed da NF-e), como na NF-e de saida.
            idIndicadorIntermediador: [null as number | null],
            cnpjIntermediador: [null as string | null, Validators.maxLength(14)],
            identificadorIntermediador: [null as string | null, Validators.maxLength(60)],

            // ---- Secao 1: dados gerais - Prazos -----------------------------------------
            // Validade e descricao do prazo sao do orcamento; envio e despacho, do pedido. A data
            // prevista de entrega e a mesma coluna nos dois.
            validadeDias: [null as number | null],
            dataPrevistaEntrega: [null as Date | string | null],
            descricaoCondicoes: [null as string | null],
            dataEnvio: [null as Date | string | null],
            dataMaximaDespacho: [null as Date | string | null],

            // ---- Secao 1: dados gerais - Cliente e Endereco de entrega ------------------
            aosCuidadosDe: [null as string | null],
            idPessoaCliente: [null as number | null, Validators.required],
            isEntregaEnderecoDiferente: [false],
            entregaCep: [null as string | null],
            entregaLogradouro: [null as string | null],
            entregaNumero: [null as string | null],
            entregaBairro: [null as string | null],
            entregaCidade: [null as string | null],
            entregaUf: [null as string | null],
            // SÓ DE TELA: o id do município escolhido no select (o contrato grava `entregaCidade`
            // como texto). Nasce desabilitado — habilita quando há UF — e sai do request no salvar.
            entregaIdMunicipio: [{ value: null as number | null, disabled: true }],
            entregaComplemento: [null as string | null],
            entregaDestinatario: [null as string | null],
            entregaIdTipoPessoa: [null as number | null],
            entregaCpfCnpj: [null as string | null],
            entregaInscricaoEstadual: [null as string | null],
            entregaTelefone: [null as string | null],

            // ---- Secao 2: introducao [ORC] ----------------------------------------------
            introducaoProposta: [null as string | null],

            // ---- Secao 3: itens ---------------------------------------------------------
            itens: this.fb.array([]),

            // ---- Secoes 4 e 5: tributos e totais ----------------------------------------
            // Os tres editaveis nascem VAZIOS (§7, "iniciando vazios"). Os valores de imposto
            // sao APURADOS, nao digitados: entram no form porque viajam no request, e quem os
            // escreve e o salvamento, a partir da mesma conta que a secao exibe.
            valorDesconto: [null as number | null],
            // Desconto do documento em %: quando informado, o servidor recalcula `valorDesconto`
            // (D5). Nulo = o que vale é o R$. A coluna `venda.percentual_desconto` ainda não
            // existe (script da Fase 6): na recarga o documento volta em R$.
            percentualDescontoGlobal: [null as number | null],
            // Só de TELA (sai do request em `montarRequest`): qual dos dois campos de desconto foi
            // o digitado. Os dois ficam sempre preenchidos; o servidor só recebe o percentual quando
            // ele foi a fonte — senão o que vale é o R$.
            isDescontoPercentualFonte: [false],
            valorFrete: [null as number | null],
            valorDespesas: [null as number | null],
            valorIpi: [0],
            valorIcms: [0],
            valorIcmsSt: [0],
            valorPis: [0],
            valorCofins: [0],
            valorDifal: [0],
            valorIbsEstadual: [0],
            valorIbsMunicipal: [0],
            valorIbsTotal: [0],
            valorCbs: [0],

            // ---- Secao 6: pagamento / condicoes comerciais ------------------------------
            pagamentos: this.fb.array([]),
            idCentroCusto: [null as number | null],
            isRateioCentroCusto: [false],
            rateioCentroCusto: this.fb.array([]),
            condicoesComerciais: [null as string | null],

            // ---- Secao 7: transporte ----------------------------------------------------
            idFormaEnvio: [null as number | null],
            idFormaFrete: [null as number | null],
            idModalidadeFrete: [null as number | null],
            idPessoaTransportador: [null as number | null],
            codigoRastreamento: [null as string | null],
            urlRastreamento: [null as string | null],
            isEnviarExpedicao: [false],
            // A tela edita UM volume, como a NF-e de saida: montado do primeiro volume gravado e
            // enviado como lista (`volumes`) no salvamento, so quando algum campo foi preenchido.
            volume: this.fb.group({
                quantidade: [null as number | null],
                especie: [null as string | null],
                marca: [null as string | null],
                numeracao: [null as string | null],
                pesoBruto: [null as number | null],
                pesoLiquido: [null as number | null],
            }),

            // ---- Secao 8: assinatura [ORC] ----------------------------------------------
            assinaturaSaudacao: [null as string | null],
            assinaturaResponsavel: [null as string | null],

            // ---- Secao 9: dados adicionais ----------------------------------------------
            observacoes: [null as string | null],
            observacoesInternas: [null as string | null],
            idsTagVenda: [[] as number[]],
        });
    }

    private carregar(): void {
        if (this.idVenda == null) return;

        this.vendaDocumentoService.getPorId(this.idVenda).subscribe({
            next: (res) => {
                this.isLoading = false;

                if (res.isError || !res.venda) {
                    this.alertService.show('Erro', res.errorDescription ?? 'Documento não encontrado.', AlertType.error);
                    this.sair();
                    return;
                }

                this.documento = res.venda;
                this.credencialRemocao = null;
                this.idVendaTipoDocumento = res.venda.idVendaTipoDocumento;

                // O card do cliente abre com o que o documento já traz; o financeiro o próprio
                // componente busca.
                this.clienteSelecionado = res.venda.idPessoaCliente
                    ? {
                          idPessoa: res.venda.idPessoaCliente,
                          nome: res.venda.nomePessoaCliente ?? '',
                          documento: res.venda.cpfCnpjPessoaCliente ?? null,
                          idTipoPessoa: res.venda.idTipoPessoaCliente ?? 0,
                      }
                    : null;

                // Os gravados entram SEM base64: o arquivo fica no storage e so desce quando o
                // usuario clica em baixar.
                this.anexos = (res.venda.anexos ?? []).map((a) => ({
                    idVendaAnexo: a.idVendaAnexo,
                    nomeArquivo: a.nomeArquivo,
                    tamanhoBytes: a.tamanhoBytes,
                }));

                // Os controles simples entram pelo patch. As COLECOES nao: `patchValue` preenche
                // controles que ja existem, e os FormArrays nascem vazios — o array do documento
                // passaria em silencio, sem erro e sem linha, e o proximo PUT apagaria tudo.
                this.form.patchValue(res.venda as unknown as Record<string, unknown>);

                // Desconto do documento (D20): R$ e % gravados juntos. O nome do campo difere entre
                // a leitura (`percentualDesconto`, como a coluna) e a escrita
                // (`percentualDescontoGlobal`, como o request) — o form usa o da escrita. Ao abrir,
                // a fonte é o R$ (é o valor que vale): itens mudando, o % é que acompanha.
                this.form.patchValue(
                    {
                        percentualDescontoGlobal: res.venda.percentualDesconto ?? null,
                        isDescontoPercentualFonte: false,
                    },
                    { emitEvent: false },
                );

                this.montarRecebimentos(res.venda);
                this.montarRateio(res.venda);
                this.montarVolume(res.venda);
                this.montarItens(res.venda.itens);

                // Avisa a seção de itens de que as linhas gravadas estão no FormArray, para ela ligar
                // o recálculo e o rateio do desconto sobre elas.
                this.itensDoDocumento = (res.venda.itens ?? []) as unknown as Record<string, unknown>[];

                this.nomesKitCarregados = {};
                for (const item of res.venda.itens ?? []) {
                    if (item.idProdutoKit != null && item.descricaoProdutoKit) {
                        this.nomesKitCarregados[item.idProdutoKit] = item.descricaoProdutoKit;
                    }
                }

                this.idsEtapaDestinoPermitida = res.idsEtapaDestinoPermitida ?? [];
                this.resolverEtapa(res.venda.idEtapaKanban);

                // Qualquer integração trava a edição, como o PUT no servidor. Mesmo texto da recusa da API.Vendas
                // (DescreverIntegracoes): um nome por tipo, na ordem do enum, com a concordância de cada um
                // (financeiro/estoque: "lançado", "o"; NF-e: "lançada", "a"); vários = "os lançamentos"; tipo fora
                // do catálogo = "lançamentos".
                const idsTipo = [...new Set((res.venda.integracoes ?? []).map((i) => i.idVendaTipoIntegracao))].sort((a, b) => a - b);
                const tipos = [...new Map(idsTipo.map((idTipo) => {
                    const nome = VendaTipoIntegracaoIndicador[idTipo]?.nome ?? null;
                    const rotulo = nome == null ? null : nome.startsWith('NF') ? nome : nome.toLowerCase();
                    return [rotulo ?? `?${idTipo}`, { rotulo, isFeminino: !!rotulo?.startsWith('NF') }] as const;
                })).values()];

                let tem: string;
                let exclua: string;
                if (tipos.some((t) => t.rotulo == null)) {
                    tem = 'lançamentos';
                    exclua = 'os lançamentos';
                } else if (tipos.length === 1) {
                    const { rotulo, isFeminino } = tipos[0];
                    tem = `${rotulo} ${isFeminino ? 'lançada' : 'lançado'}`;
                    exclua = `${isFeminino ? 'a' : 'o'} ${rotulo}`;
                } else {
                    const nomes = tipos.map((t) => t.rotulo as string);
                    const lista = `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
                    tem = `${lista} ${tipos.every((t) => t.isFeminino) ? 'lançadas' : 'lançados'}`;
                    exclua = 'os lançamentos';
                }

                this.motivoLeituraIntegracao = tipos.length
                    ? `Este documento tem ${tem}. Para editar, exclua ${exclua} pelo menu Ações.`
                    : null;

                if (this.motivoLeituraIntegracao) this.isSomenteLeitura = true;

                this.aplicarValidadoresDaConfiguracao();
                this.aplicarSecoesRecolhidasDaConfiguracao();

                if (this.isSomenteLeitura) {
                    this.form.disable({ emitEvent: false });
                    this.isFormDesabilitadoPorLeitura = true;
                } else if (this.isFormDesabilitadoPorLeitura) {
                    // Deixou de ser somente-leitura (estoque ou financeiro excluído): o form volta, e as seções, recriadas
                    // pelo isLoading de quem recarregou, reaplicam as próprias travas no ngOnInit.
                    this.form.enable({ emitEvent: false });
                    this.isFormDesabilitadoPorLeitura = false;
                }

                // O que veio do servidor nao e alteracao do usuario.
                this.form.markAsPristine();
            },
            error: (err) => {
                this.isLoading = false;
                this.alertService.show('Aviso', err.message, AlertType.warning);
            },
        });
    }

    private montarRecebimentos(venda: VendaDetalheStruct): void {
        this.pagamentos.clear({ emitEvent: false });

        for (const recebimento of venda.pagamentos ?? []) {
            this.pagamentos.push(montarRecebimentoDoServidor(this.fb, recebimento), { emitEvent: false });
        }
    }

    private montarRateio(venda: VendaDetalheStruct): void {
        this.rateioCentroCusto.clear({ emitEvent: false });

        for (const linha of venda.rateioCentroCusto ?? []) {
            this.rateioCentroCusto.push(montarRateioDoServidor(this.fb, linha), { emitEvent: false });
        }
    }

    /**
     * Linhas dos itens GRAVADOS no FormArray (task 148606), montadas aqui, no `subscribe`, e não na
     * seção de itens: a seção recebe o documento por `@Input`, e montar as linhas no setter
     * acontecia DURANTE a detecção, depois de este template já ter lido `quantidadeDeItens` para o
     * badge do título (NG0100). `form.patchValue()` não cria controles de FormArray, por isso o
     * `push` explícito. A seção liga o recálculo e o rateio quando `itensDoDocumento` chega.
     */
    private montarItens(itens: VendaDetalheStruct['itens'] | null | undefined): void {
        this.itens.clear({ emitEvent: false });

        for (const item of itens ?? []) {
            this.itens.push(montarItemDoServidor(this.fb, item as unknown as Record<string, unknown>), { emitEvent: false });
        }
    }

    /** A tela edita o primeiro volume gravado (`numeroVolume` 1), como a NF-e de saída. */
    private montarVolume(venda: VendaDetalheStruct): void {
        const volume = venda.volumes?.[0];

        this.form.get('volume')?.reset(
            {
                quantidade: volume?.quantidade ?? null,
                especie: volume?.especie ?? null,
                marca: volume?.marca ?? null,
                numeracao: volume?.numeracao ?? null,
                pesoBruto: volume?.pesoBruto ?? null,
                pesoLiquido: volume?.pesoLiquido ?? null,
            },
            { emitEvent: false },
        );
    }

    /**
     * A etapa atual, resolvida no catalogo do Kanban que o agregado ja trouxe — sem requisicao.
     *
     * `isFinal` e configuravel pela empresa (Configuracoes ▸ Vendas ▸ Etapas do kanban de vendas), e e ela
     * que decide o encerramento; o "Modelo" entra fixo porque e template, nao documento.
     */
    private resolverEtapa(idEtapaKanban: number | null | undefined): void {
        const etapa = idEtapaKanban
            ? this.processosKanban.flatMap((p) => p.etapas ?? []).find((s) => s.idEtapaKanban === idEtapaKanban) ?? null
            : null;

        this.etapaAtual = etapa;
        this.corEtapa = etapa ? resolverCor(etapa.idKanbanCor, this.coresKanban, this.isDarkMode) : null;
        this.iconeEtapa = etapa
            ? this.iconesKanban.find((i) => i.idKanbanIcone === etapa.idKanbanIcone)?.nomeIcone ?? null
            : null;
        this.isEncerrado = !!etapa && (etapa.isFinal || etapa.chave === CHAVE_ETAPA_MODELO);
        this.isSomenteLeitura = this.isEncerrado;
        this.destinosDeEtapa = this.montarDestinosDeEtapa();
    }

    /** Os destinos permitidos pelo servidor, com o visual e a legenda do protótipo ("Exige motivo" ou o processo). */
    private montarDestinosDeEtapa(): DestinoDeEtapa[] {
        const destinos: DestinoDeEtapa[] = [];

        for (const processo of this.processosKanban) {
            for (const etapa of processo.etapas ?? []) {
                if (!this.idsEtapaDestinoPermitida.includes(etapa.idEtapaKanban)) continue;

                destinos.push({
                    etapa,
                    legenda: etapa.isMotivoObrigatorio ? 'Exige motivo' : processo.descricaoTipo,
                    cor: resolverCor(etapa.idKanbanCor, this.coresKanban, this.isDarkMode),
                    icone: this.iconesKanban.find((i) => i.idKanbanIcone === etapa.idKanbanIcone)?.nomeIcone ?? null,
                });
            }
        }

        return destinos;
    }

    /**
     * Mover o documento pelo campo Etapa (§9). Move o que está GRAVADO — por isso, com alteração
     * pendente na tela, pede para salvar antes: mover o documento antigo e continuar editando o
     * rascunho seria salvar depois por cima de uma etapa que já mudou.
     *
     * Motivo obrigatório usa o mesmo painel do board; a regra (destino habilitado, perfil, motivo,
     * liberação de bloqueio) é toda do servidor, e a recusa dele volta como alerta.
     */
    public moverEtapa(destino: EtapaKanbanStruct): void {
        if (this.idVenda == null || this.isMovendoEtapa) return;

        if (this.temAlteracoesPendentes) {
            this.alertService.show('Salve antes', 'Salve as alterações do documento antes de mudar a etapa.', AlertType.warning);
            return;
        }

        if (!destino.isMotivoObrigatorio) {
            this.pedirLiberacaoSeVencido(destino, (liberacao) => this.executarMovimento(destino, null, null, liberacao));
            return;
        }

        // Lista vinculada vazia = qualquer motivo ativo serve. Mesma convenção do board e do servidor.
        const motivos = destino.idsMotivoTransicao.length > 0
            ? this.motivosKanban.filter((m) => destino.idsMotivoTransicao.includes(m.idMotivoTransicao))
            : this.motivosKanban;

        this.dialog
            .open(PvMotivoDialogComponent, { data: { nomeDestino: destino.nome, motivos } as PvMotivoDialogData })
            .afterClosed()
            .subscribe((resultado: PvMotivoDialogResult | undefined) => {
                if (!resultado) return;

                this.pedirLiberacaoSeVencido(destino, (liberacao) =>
                    this.executarMovimento(destino, resultado.idMotivoTransicao, resultado.justificativa, liberacao));
            });
    }

    /**
     * §12.7: orçamento vencido só converte em pedido com liberação de um supervisor — o mesmo diálogo do salvar e do
     * board. Conversão é o destino em outro processo (a regra do servidor); master não pede liberação a si mesmo.
     */
    private pedirLiberacaoSeVencido(destino: EtapaKanbanStruct, aoSeguir: (liberacao: LiberacaoPresencialStruct | null) => void): void {
        const ehConversao = this.ehOrcamento && this.etapaAtual != null
            && destino.idConfigKanbanProcesso !== this.etapaAtual.idConfigKanbanProcesso;
        // "Vencido" vem do servidor (mesma regra do card e da API.Vendas): a tela não decide pelo relógio do navegador.
        const dataValidade = this.documento?.dataValidade ?? null;

        if (!ehConversao || !dataValidade || !this.documento?.isVencido || this.authService.getUserInfos()?.master) {
            aoSeguir(null);
            return;
        }

        const bloqueios = [bloqueioDeOrcamentoVencido(dataValidade)];

        this.dialog
            .open<VdLiberacaoDialogComponent, VdLiberacaoDialogData, VdLiberacaoDialogResult>(VdLiberacaoDialogComponent, {
                data: { bloqueios },
                width: '640px',
                maxWidth: '96vw',
                autoFocus: false,
                disableClose: true,
            })
            .afterClosed()
            .subscribe((credencial) => {
                if (!credencial) {
                    this.alertService.show('Documento não movido', 'A liberação foi cancelada e o orçamento continua na etapa atual.', AlertType.warning);
                    return;
                }

                aoSeguir({
                    email: credencial.email,
                    senha: credencial.senha,
                    observacao: credencial.observacao,
                    motivos: motivosDaLiberacao(bloqueios),
                });
            });
    }

    /**
     * `confirmacao` é o segundo passo do voltar etapa ou cancelar com integrações (T17): o usuário viu o alerta e disse o
     * que desfazer; o mesmo movimento é reaplicado com a flag e a escolha.
     */
    private executarMovimento(
        destino: EtapaKanbanStruct,
        idMotivoTransicao: number | null,
        justificativa: string | null,
        liberacaoPresencial: LiberacaoPresencialStruct | null = null,
        confirmacao: PvConfirmarMovimentoDialogResult | null = null,
    ): void {
        const request = new MoverVendaEtapaRequest();
        request.idVenda = this.idVenda!;
        request.idEtapaKanbanDestino = destino.idEtapaKanban;
        request.idMotivoTransicao = idMotivoTransicao;
        request.justificativa = justificativa;
        // A senha vive só neste request: o orquestrador confere no /Auth e zera o campo.
        request.liberacaoPresencial = liberacaoPresencial;
        request.isMovimentoConfirmado = confirmacao != null;
        request.idsVendaTipoIntegracaoDesfazer = confirmacao?.idsVendaTipoIntegracaoDesfazer ?? [];

        this.isMovendoEtapa = true;

        this.processoVendaService.moverEtapa(request).subscribe({
            next: (res) => {
                this.isMovendoEtapa = false;

                // T17: pedido com lançamentos voltando de etapa ou indo para Cancelado. Nada foi gravado: o alerta pergunta
                // o que desfazer e, confirmando, o movimento é reaplicado com a escolha (o mesmo diálogo do board).
                if (res.isError && res.errorCode === MoverVendaEtapaErrorCodeEnum.ConfirmarIntegracoes) {
                    this.dialog
                        .open<PvConfirmarMovimentoDialogComponent, PvConfirmarMovimentoDialogData, PvConfirmarMovimentoDialogResult>(
                            PvConfirmarMovimentoDialogComponent,
                            {
                                data: {
                                    ...cabecalhoDoMovimento(destino.chave, destino.nome),
                                    rotuloDocumento: rotuloDocumento(res.numero, this.idVendaTipoDocumento),
                                    integracoes: res.integracoes,
                                },
                                autoFocus: false,
                            },
                        )
                        .afterClosed()
                        .subscribe((escolha) => {
                            if (!escolha) {
                                this.alertService.show('Documento não movido', 'O pedido continua na etapa atual.', AlertType.warning);
                                return;
                            }
                            this.executarMovimento(destino, idMotivoTransicao, justificativa, liberacaoPresencial, escolha);
                        });
                    return;
                }

                if (res.isError) {
                    this.alertService.show('Atenção', res.errorDescription, AlertType.warning);
                    return;
                }

                // Conversão muda o DOCUMENTO (orçamento vira pedido): o recarregamento traz o tipo,
                // o título, a etapa e o somente-leitura já certos — nada é remendado em memória.
                this.alertService.show(
                    res.isConversao ? 'Convertido' : 'Etapa alterada',
                    res.isConversao ? `Orçamento convertido em pedido de venda, em "${destino.nome}".` : `Documento movido para "${destino.nome}".`,
                    AlertType.success,
                );
                this.avisarAutomacoes(res.avisosAutomacao);
                this.isLoading = true;
                this.carregar();
            },
            error: (err) => {
                this.isMovendoEtapa = false;
                this.alertService.show('Aviso', err.message, AlertType.warning);
            },
        });
    }

    /** O servidor gravou ou moveu; a automação que não pôde rodar (reserva de estoque) vira aviso e ocorrência. */
    private avisarAutomacoes(avisos: string[] | null | undefined): void {
        for (const aviso of avisos ?? []) this.alertService.show('Aviso', aviso, AlertType.warning);
    }

    private get isDarkMode(): boolean {
        return this.themeService.isDarkMode;
    }

    /**
     * Pedido de venda para cliente com título vencido, quando a empresa configurou o bloqueio
     * (`config_pedido_venda.is_bloquear_cliente_atraso`). Orçamento nunca barra.
     */
    public get clienteBarradoPorAtraso(): boolean {
        if (this.ehOrcamento) return false;
        if (this.configPedidoVenda?.isBloquearClienteAtraso !== true) return false;

        return this.clienteFinanceiro?.temTitulosEmAtraso === true;
    }

    public salvar(): void {
        if (this.isSomenteLeitura) return;

        // §15: as pendências de tela saem numa passada só. O toast mostra a primeira (uma mensagem por
        // vez), a faixa do topo lista todas e a tela rola até a seção da primeira — o aviso aparecia no
        // canto enquanto o usuário olhava outra seção (Victor, 30/10).
        const linhas = this.itens.controls as FormGroup[];
        const pendencias = this.pendenciasDoSalvarAtuais(linhas);

        if (pendencias.length > 0) {
            this.pendenciasDoSalvar = pendencias;
            this.alertService.show(pendencias[0].titulo, pendencias[0].mensagem, AlertType.warning);
            this.irParaSecao(pendencias[0].secao);
            return;
        }

        this.pendenciasDoSalvar = [];

        const request = this.montarRequest(linhas);

        // Quem tem alçada para liberar não pede liberação a si mesmo (Pedro, 15/10): o master libera
        // qualquer bloqueio, então salva direto. A folha e o orquestrador aplicam a mesma isenção (teto
        // de desconto e título em atraso).
        if (this.authService.getUserInfos()?.master) {
            this.enviar(request, linhas);
            return;
        }

        // §6.5 — o que o servidor recusaria (teto, crédito, atraso) e um supervisor pode liberar
        // na hora. Detectado AQUI, antes de enviar, para o diálogo pedir a credencial uma vez só,
        // com todos os motivos juntos — e não uma recusa por motivo.
        const bloqueios = detectarBloqueios({
            linhas,
            tetoDesconto: this.tetoDesconto,
            temDescontoDoDocumento: Number(this.form.get('valorDesconto')?.value ?? 0) > 0,
            ehPedido: !this.ehOrcamento,
            clienteBarradoPorAtraso: this.clienteBarradoPorAtraso,
            cliente: this.clienteFinanceiro,
            totalDoDocumento: this.totalDoDocumento,
            valorSujeitoALimite: this.valorSujeitoALimite,
            itensGravadosRemovidos: this.podeRemoverItem || this.idVenda == null ? [] : this.itensGravadosRemovidos(linhas),
            liberacoes: this.documento?.liberacoes ?? [],
        });

        if (bloqueios.length === 0) {
            this.enviar(request, linhas);
            return;
        }

        // T03: a credencial coletada ao remover o item gravado vale para este salvar sem reabrir o
        // diálogo — só quando a remoção é o ÚNICO motivo. Com outro bloqueio junto, o diálogo pede
        // uma vez para todos (uma credencial cobre tudo) e a guardada é descartada.
        const soRemocao = bloqueios.every((b) => b.idVendaTipoBloqueio === VendaTipoBloqueioEnum.RemocaoDeItem);

        if (soRemocao && this.credencialRemocao) {
            request.liberacaoPresencial = {
                email: this.credencialRemocao.email,
                senha: this.credencialRemocao.senha,
                observacao: this.credencialRemocao.observacao,
                motivos: motivosDaLiberacao(bloqueios),
            };

            this.enviar(request, linhas);
            return;
        }

        this.credencialRemocao = null;

        this.dialog
            .open<VdLiberacaoDialogComponent, VdLiberacaoDialogData, VdLiberacaoDialogResult>(VdLiberacaoDialogComponent, {
                data: { bloqueios },
                width: '640px',
                maxWidth: '96vw',
                autoFocus: false,
                disableClose: true,
            })
            .afterClosed()
            .subscribe((credencial) => {
                if (!credencial) {
                    this.alertService.show('Documento não salvo', 'A liberação foi cancelada e o documento continua bloqueado.', AlertType.warning);
                    return;
                }

                // A senha vive só neste request: o orquestrador confere no /Auth e zera o campo.
                request.liberacaoPresencial = {
                    email: credencial.email,
                    senha: credencial.senha,
                    observacao: credencial.observacao,
                    motivos: motivosDaLiberacao(bloqueios),
                };

                this.enviar(request, linhas);
            });
    }

    private enviar(request: SalvarVendaRequest, linhas: FormGroup[]): void {
        this.isSalvando = true;

        const chamada =
            this.idVenda == null
                ? this.vendaDocumentoService.incluir(request)
                : this.vendaDocumentoService.alterar(this.idVenda, request);

        chamada.subscribe({
            next: (res) => {
                this.isSalvando = false;

                if (res.isError) {
                    // Regra de negocio recusada pelo servidor: o usuario resolve na tela. A credencial
                    // da remoção (T03) não sobrevive à recusa: o próximo salvar pede de novo.
                    this.credencialRemocao = null;
                    this.alertService.show('Não foi possível salvar', res.errorDescription ?? '', AlertType.warning);
                    // O documento pode ter sido gravado (anexo que falhou): os avisos das automações não se perdem.
                    this.avisarAutomacoes(res.avisosAutomacao);
                    return;
                }

                this.credencialRemocao = null;

                // O numero so existe depois de gravar, na numeracao Sequencial — por isso volta
                // na resposta e nao e adivinhado pela tela.
                const eraNovo = this.idVenda == null;

                this.idVenda = res.idVenda;
                this.idVendaOrigem = null;
                this.rotuloOrigem = null;
                this.form.patchValue({ numero: res.numero }, { emitEvent: false });
                this.form.markAsPristine();
                this.avisarPendenciasInformativas(linhas);

                // Documento criado pela ficha do CRM: volta para ela, que já mostra o documento na aba Vendas.
                if (eraNovo && this.idOportunidadeDaUrl != null) {
                    this.alertService.show('Salvo', `${this.titulo} gravado.`, AlertType.success);
                    this.voltarParaOportunidade();
                    return;
                }

                // A URL passa a ser a do documento gravado: um F5 na rota "novo" abriria um
                // documento em branco, como se o salvamento nao tivesse acontecido.
                if (eraNovo) this.location.replaceState(`/vendas/processo-venda/documento/${res.idVenda}`);

                // Recarrega SEMPRE: o servidor decidiu coisas que a tela nao tem como saber —
                // etapa e sequencia no novo, ids novos dos filhos (apaga e regrava) e dos anexos
                // que subiram. Sem isso a tela reenviaria base64 e ids velhos no proximo salvar.
                this.isLoading = true;
                this.carregar();

                this.alertService.show('Salvo', `${this.titulo} gravado.`, AlertType.success);
                this.avisarAutomacoes(res.avisosAutomacao);
            },
            error: (err) => {
                this.isSalvando = false;
                this.alertService.show('Aviso', err.message, AlertType.warning);
            },
        });
    }

    /**
     * As pendências de tela do salvar, na ordem da história (§15): campos obrigatórios, item sem produto,
     * múltiplo, unidade que não fraciona e pagamento. Cada uma sabe a seção onde se resolve.
     */
    private pendenciasDoSalvarAtuais(linhas: FormGroup[]): PendenciaDoSalvar[] {
        const pendencias: PendenciaDoSalvar[] = [];

        // §15.1: pendencia que o usuario resolve preenchendo a tela e ATENCAO, nao erro — a cor
        // vermelha fica para o que fugiu do controle dele.
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            pendencias.push({
                secao: this.secaoDoPrimeiroCampoInvalido(),
                titulo: 'Campos obrigatórios',
                mensagem: 'Preencha os campos destacados antes de salvar.',
            });
        }

        // Linha sem produto nao e item (§6.2.1): nao vai para o servidor — e nao e descartada em
        // silencio, porque pode ser um item que o usuario ainda ia preencher.
        const semProduto = linhas.filter((linha) => !linha.get('dados.idProduto')?.value).length;

        if (semProduto > 0) {
            pendencias.push({
                secao: 'itens',
                titulo: 'Item sem produto',
                mensagem: `${semProduto} ${semProduto === 1 ? 'linha está' : 'linhas estão'} sem produto. Escolha o produto ou remova a linha.`,
            });
        }

        // §6.2.3 / §15: quantidade que não é múltipla do fator só chega por outra via (documento
        // antigo, importação) — a grade não deixa digitar. Mesma conta e mesma mensagem do servidor.
        const pendenciaDeMultiplo = this.pendenciaDeMultiplo(linhas);

        if (pendenciaDeMultiplo) {
            pendencias.push({ secao: 'itens', titulo: 'Quantidade fora do múltiplo', mensagem: pendenciaDeMultiplo });
        }

        // Unidade que não fraciona: a máscara inteira barra a digitação, mas o componente de kit
        // (composição × kits) e o documento antigo chegam com fração. Mesma regra e mensagem do servidor.
        const pendenciaDeQuantidadeFracionada = this.pendenciaDeQuantidadeFracionada(linhas);

        if (pendenciaDeQuantidadeFracionada) {
            pendencias.push({ secao: 'itens', titulo: 'Quantidade fracionada', mensagem: pendenciaDeQuantidadeFracionada });
        }

        // §8.0 e §8.10: forma em todos os recebimentos, condicao e conta onde a forma exige,
        // soma igual ao total, rateio fechando 100%. Funcao pura sobre o form: o shell nao
        // pergunta ao filho se esta valido — comunicacao entre componentes e por Input/Output.
        const pendenciaDePagamento = validarPagamento(this.form, {
            ehOrcamento: this.ehOrcamento,
            totalDoDocumento: this.totalDoDocumento,
            formas: this.financas?.listaFormaRecebimento ?? [],
            isBloquearSemFormaRecebimento: this.configPedidoVenda?.isBloquearSemFormaRecebimento === true,
            contas: this.financas?.listaContaFinanceira ?? [],
            intermediadores: this.financas?.listaIntermediadorPagamento ?? [],
            camposObrigatoriosContasReceber: this.camposObrigatoriosContasReceber,
        });

        if (pendenciaDePagamento) {
            pendencias.push({ secao: 'pagamento', ...pendenciaDePagamento });
        }

        return pendencias;
    }

    /**
     * Seção do primeiro campo inválido, pelo DOM: o form é plano e não sabe de seções, mas cada campo
     * vive dentro de um `.section-container` com o id da seção. Sem achar, Dados (onde ficam os obrigatórios).
     */
    private secaoDoPrimeiroCampoInvalido(): SecaoDocumento {
        const secao = this.elementRef.nativeElement.querySelector<HTMLElement>('.section-container:has(.ng-invalid)');

        return (secao?.id as SecaoDocumento | undefined) ?? 'dados';
    }

    /** Rola até a seção pela barra, que também a expande quando está recolhida. */
    public irParaSecao(secao: SecaoDocumento): void {
        this.barraSecoes?.scrollToSection(secao);
    }

    private pendenciaDeMultiplo(linhas: FormGroup[]): string | null {
        const casas = this.mascaraQuantidade.precision ?? 2;

        for (const linha of linhas) {
            if (linha.get('kit.numeroKit')?.value != null) continue;

            const regra = regraDoMultiplo({
                isControlaMultiplo: linha.get('multiplo.isControlaMultiplo')?.value,
                fatorMultiplo: linha.get('multiplo.fatorMultiplo')?.value,
                unidadeMultiplo: linha.get('multiplo.unidadeMultiplo')?.value,
            });

            if (!regra) continue;

            if (!quantidadeEhMultipla(Number(linha.get('dados.quantidade')?.value ?? 0), regra.fatorMultiplo, casas)) {
                return mensagemDoMultiplo(linha.get('dados.descricao')?.value ?? 'O item', regra, casas);
            }
        }

        return null;
    }

    /** Espelho do `ValidarQuantidadeFracionada` do orquestrador; o componente de kit entra. */
    private pendenciaDeQuantidadeFracionada(linhas: FormGroup[]): string | null {
        for (const linha of linhas) {
            if (linha.get('dados.isUnidadeMedidaFracionada')?.value !== false) continue;

            if (Number(linha.get('dados.quantidade')?.value ?? 0) % 1 !== 0) {
                const nome = linha.get('dados.descricao')?.value ?? '';

                return `O produto "${nome}" tem unidade de medida não fracionada - a quantidade deve ser um número inteiro.`;
            }
        }

        return null;
    }

    /**
     * Avisos INFORMATIVOS do pedido (Configurações ▸ Vendas ▸ Pedido de venda, §3.1): não barram
     * o salvar — depois de gravado, contam ao vendedor o que o cadastro do cliente ou a comissão
     * têm de estranho. Comissão zerada só é avaliada com vendedor informado.
     */
    private avisarPendenciasInformativas(linhas: FormGroup[]): void {
        if (this.ehOrcamento) return;

        if (this.configPedidoVenda?.isAlertaEnderecoIncompleto === true && this.enderecoDoClienteIncompleto) {
            this.alertService.show(
                'Endereço do cliente incompleto',
                'O pedido foi salvo, mas o cadastro do cliente está sem CEP, logradouro, número, bairro ou cidade. Complete o endereço antes de emitir a nota ou despachar.',
                AlertType.info,
            );
        }

        const temVendedor = !!this.form.get('idVendedor')?.value;
        const comissaoZerada = linhas.length > 0
            && linhas.every((l) => !(Number(l.get('comissao.valorComissao')?.value ?? 0) > 0));

        if (this.configPedidoVenda?.isAlertaComissaoZerada === true && temVendedor && comissaoZerada) {
            this.alertService.show(
                'Comissão zerada',
                'O pedido foi salvo, mas nenhum item gera comissão para o vendedor. Confira a regra de comissão do vendedor e os descontos aplicados.',
                AlertType.info,
            );
        }
    }

    /** Endereço completo = CEP, logradouro, número, bairro e cidade preenchidos no cadastro. */
    private get enderecoDoClienteIncompleto(): boolean {
        const c = this.clienteFinanceiro;

        if (!c) return false;

        return !c.cep || !c.logradouro || !c.numero || !c.bairro || !c.localidade;
    }

    /**
     * O corpo do POST/PUT, TIPADO — sem `as unknown as`: cada divergencia entre o form e o
     * contrato aparece aqui, em compilacao, e nao como 400 em producao.
     *
     * O form e ANINHADO (itens em oito grupos, recebimentos com parcelas dentro) e o contrato e
     * plano por tabela; os mapas fazem o achatamento. Os numericos que o C# declara nao-anulaveis
     * (`numero`, ids dos filhos) vao como 0 quando o form ainda nao os tem — o servidor os atribui.
     */
    private montarRequest(linhas: FormGroup[]): SalvarVendaRequest {
        // Os impostos do cabecalho sao a MESMA conta que a secao de Totais exibe. Escritos no form
        // antes do getRawValue para a tela e o request nunca discordarem.
        const totais = calcularTotaisDoDocumento(this.itens);

        this.form.patchValue(
            {
                valorIpi: totais.valorIpi,
                valorIcms: totais.valorIcms,
                valorIcmsSt: totais.valorIcmsSt,
                valorPis: totais.valorPis,
                valorCofins: totais.valorCofins,
                valorDifal: totais.valorDifal,
                valorIbsEstadual: totais.valorIbsEstadual,
                valorIbsMunicipal: totais.valorIbsMunicipal,
                valorIbsTotal: totais.valorIbsTotal,
                valorCbs: totais.valorCbs,
            },
            { emitEvent: false },
        );

        const { isDescontoPercentualFonte, entregaIdMunicipio, volume, ...bruto } = this.form.getRawValue();

        // Volume só viaja com algum campo preenchido; ids, empresa e inclusão são do servidor.
        const temVolume = Object.values(volume ?? {}).some((valor) => valor != null && valor !== '');

        // O cliente trocado na tela tem o nome no card financeiro; o gravado, no `clienteSelecionado`.
        let nomePessoaCliente: string | null = null;

        if (this.clienteFinanceiro?.idPessoa === bruto.idPessoaCliente) nomePessoaCliente = this.clienteFinanceiro?.nome ?? null;
        else if (this.clienteSelecionado?.idPessoa === bruto.idPessoaCliente) nomePessoaCliente = this.clienteSelecionado?.nome || null;

        // Kits da grade com o nome da linha; os que saíram ficam com o nome do documento carregado.
        const nomesKit: Record<number, string> = { ...this.nomesKitCarregados };

        for (const linha of linhas) {
            const idProdutoKit = linha.get('kit.idProdutoKit')?.value as number | null;
            const descricaoKit = linha.get('kit.descricaoKit')?.value as string | null;
            if (idProdutoKit != null && descricaoKit) nomesKit[idProdutoKit] = descricaoKit;
        }

        return {
            ...bruto,
            numero: Number(bruto.numero ?? 0) || 0,
            // D20: R$ e % viajam SEMPRE juntos; o servidor grava os dois sem recalcular (o R$ é o que vale).
            percentualDescontoGlobal: bruto.percentualDescontoGlobal ?? null,
            dataEmissao: paraDataDeCalendario(bruto.dataEmissao) ?? '',
            dataProximoContato: paraDataDeCalendario(bruto.dataProximoContato),
            dataPrevistaEntrega: paraDataDeCalendario(bruto.dataPrevistaEntrega),
            dataEnvio: paraDataDeCalendario(bruto.dataEnvio),
            // Derivada no servidor; a tela nunca a informa (Prazos do pedido).
            dataMaximaDespacho: null,
            cnpjIntermediador: bruto.cnpjIntermediador || null,
            // O campo guarda o documento formatado (MaskService.onDocumentoInput); o contrato, sem separadores.
            entregaCpfCnpj: bruto.entregaCpfCnpj ? String(bruto.entregaCpfCnpj).replace(/[.\-\/]/g, '') : null,
            identificadorIntermediador: bruto.identificadorIntermediador || null,
            volumes: temVolume ? [{ numeroVolume: 1, ...volume }] : [],
            itens: linhas.map((linha) => achatarItemParaRequest(linha)),
            pagamentos: (this.pagamentos.controls as FormGroup[]).map(achatarRecebimentoParaRequest),
            rateioCentroCusto: bruto.isRateioCentroCusto
                ? (this.rateioCentroCusto.controls as FormGroup[]).map(achatarRateioParaRequest)
                : [],
            idsTagVenda: bruto.idsTagVenda ?? [],
            anexos: this.anexos,
            // O que vier aqui o orquestrador descarta: liberacao so nasce de `liberacaoPresencial`.
            liberacoes: [],
            // Preenchido pelo `salvar()` quando o dialogo de liberacao (§6.5.1) autoriza.
            liberacaoPresencial: null,
            // Resolvido e sobrescrito pelo orquestrador; o corpo nunca o informa.
            idsVendedorPermitidos: null,
            // Clonar (148656): so vale na criacao, e so para o texto da ocorrencia.
            idVendaOrigem: this.idVenda == null ? this.idVendaOrigem : null,
            // Ficha do CRM (150708): o vinculo nasce so na criacao; a alteracao o ignora.
            idOportunidade: this.idVenda == null ? this.idOportunidadeDaUrl : null,
            // So informativos (texto da ocorrencia de edicao).
            nomePessoaCliente,
            // Resolvido no lookup da secao Dados, que o sobe a cada troca e ao carregar.
            nomeListaPreco: bruto.idListaPreco != null ? this.nomeListaPrecoSelecionada : null,
            nomesKit,
        };
    }

    /**
     * Historico do documento. Reusa o painel do board — e a mesma informacao, e o prototipo o
     * abre em modal tambem aqui.
     */
    // ================================================================== clonar (148656)

    /**
     * Abre a ORIGEM como documento novo: o mesmo GET do `carregar()`, com o conteúdo zerado do que
     * é do documento gravado — id, número, etapa, anexos, liberações, datas e rastreio de
     * envio. Nada é criado aqui: o usuário revisa e salva, e o POST decide o resto (etapa inicial,
     * número, travas da configuração e todas as validações).
     */
    private carregarOrigemDoClone(idVendaOrigem: number): void {
        this.vendaDocumentoService.getPorId(idVendaOrigem).subscribe({
            next: (res) => {
                this.isLoading = false;

                if (res.isError || !res.venda) {
                    this.alertService.show('Erro', res.errorDescription ?? 'Documento de origem não encontrado.', AlertType.error);
                    this.sair();
                    return;
                }

                const origem = res.venda;
                const copia = this.montarCopia(origem);

                this.rotuloOrigem = `${this.nomeDoTipo(origem.idVendaTipoDocumento).toLowerCase()} nº ${formatarNumeroDocumento(origem.numero, origem.idVendaTipoDocumento)}`;

                this.clienteSelecionado = copia.idPessoaCliente
                    ? {
                          idPessoa: copia.idPessoaCliente,
                          nome: copia.nomePessoaCliente ?? '',
                          documento: copia.cpfCnpjPessoaCliente ?? null,
                          idTipoPessoa: copia.idTipoPessoaCliente ?? 0,
                      }
                    : null;

                this.anexos = [];

                this.form.patchValue(copia as unknown as Record<string, unknown>);
                this.form.patchValue(
                    {
                        idVendaTipoDocumento: this.idVendaTipoDocumento,
                        numero: null,
                        percentualDescontoGlobal: copia.percentualDesconto ?? null,
                        isDescontoPercentualFonte: false,
                    },
                    { emitEvent: false },
                );

                this.montarRecebimentos(copia);
                this.montarRateio(copia);
                this.montarVolume(copia);
                this.montarItens(copia.itens);
                this.itensDoDocumento = (copia.itens ?? []) as unknown as Record<string, unknown>[];

                this.aplicarValidadoresDaConfiguracao();
                this.aplicarSecoesRecolhidasDaConfiguracao();

                // É conteúdo que ainda não existe no banco: a guarda de saída vale desde já.
                this.form.markAsDirty();

                this.alertService.show(
                    'Cópia',
                    `Cópia do ${this.rotuloOrigem}. Revise e salve para gerar o novo documento.`,
                    AlertType.info,
                );
            },
            error: (err) => {
                this.isLoading = false;
                this.alertService.show('Aviso', err.message, AlertType.warning);
            },
        });
    }

    /**
     * "Incluir orçamento" e "Criar pedido de venda" do cadastro de Pessoa (IV-01 a IV-06): o
     * documento novo, em branco e com os padrões da configuração, recebe o cliente, o vendedor e
     * o "Aos cuidados de". Pessoa inativa ou sem o tipo Cliente não entra (PA-08): a busca de
     * cliente da tela também não a ofereceria. Nada é criado aqui; fechar sem salvar não grava.
     */
    private iniciarDaPessoa(idPessoa: number, oportunidade: OportunidadeKanbanItemComValorStruct | null = null): void {
        this.pessoaService.getById(idPessoa).subscribe({
            next: (res) => {
                this.isLoading = false;
                // A URL da oportunidade fica: o F5 repete o preenchimento e mantém o vínculo.
                if (!oportunidade) this.limparPessoaDaUrl();

                const pessoa = res.pessoa;

                if (res.isError || !pessoa) {
                    this.alertService.show(
                        'Aviso',
                        res.errorDescription || 'Não foi possível carregar a pessoa para iniciar o documento.',
                        AlertType.warning,
                    );
                    return;
                }

                if (!pessoa.isAtivo) {
                    this.alertService.show(
                        'Aviso',
                        `${pessoa.nome} está inativo no cadastro e não pode ser usado como cliente.`,
                        AlertType.warning,
                    );
                    return;
                }

                if (!(res.listIdTipoContato ?? []).includes(TipoContatoEnum.Cliente)) {
                    this.alertService.show(
                        'Aviso',
                        `Inclua o tipo Cliente no cadastro de ${pessoa.nome} para vender a esta pessoa.`,
                        AlertType.warning,
                    );
                    return;
                }

                // Como o clone: o `vd-cliente` abre o card e busca o financeiro sozinho. O documento
                // vai cru, como na busca de cliente (CPF, senão CNPJ); o card formata.
                this.clienteSelecionado = {
                    idPessoa: pessoa.idPessoa,
                    nome: pessoa.nome,
                    documento: pessoa.cnpj || pessoa.cpf || pessoa.documentoEstrangeiro || null,
                    idTipoPessoa: pessoa.idTipoPessoa,
                };

                this.form.patchValue({ idPessoaCliente: pessoa.idPessoa });

                // Igual a uma escolha manual: a guarda de saída vale desde já.
                this.form.markAsDirty();

                if (oportunidade) {
                    this.aplicarDadosDaOportunidade(oportunidade);
                    return;
                }

                // IV-04/IA-02: pessoa com vendedor usa o dele; se a tela não o oferece (escopo do
                // usuário), fica vazio para escolher. Pessoa sem vendedor mantém o padrão já aplicado
                // (o do usuário logado) ou vazio.
                if (pessoa.idVendedor != null) {
                    const vendedorDaPessoa = this.vendedores.find((v) => v.idVendedor === pessoa.idVendedor);

                    this.form.patchValue({ idVendedor: vendedorDaPessoa?.idVendedor ?? null });
                }

                // "Aos cuidados de": o primeiro contato adicional, na ordem do cadastro.
                const primeiroContato = res.contatosAdicionais?.[0]?.nome;

                if (primeiroContato) this.form.patchValue({ aosCuidadosDe: primeiroContato });

                this.isOrigemPessoa = true;
            },
            error: () => {
                this.isLoading = false;
                if (!oportunidade) this.limparPessoaDaUrl();
                this.alertService.show(
                    'Aviso',
                    'Não foi possível carregar a pessoa para iniciar o documento agora. Tente novamente em alguns instantes.',
                    AlertType.warning,
                );
            },
        });
    }

    /**
     * Criar orçamento ou pedido pela ficha do CRM: carrega a oportunidade e entra no mesmo fluxo da pessoa
     * (cliente pelo id, com as mesmas validações), com vendedor e "Aos cuidados de" vindos da oportunidade.
     * Sem a oportunidade, a tela segue em branco, ainda ligada a ela (o orquestrador valida no salvar).
     */
    private iniciarDaOportunidade(idOportunidade: number): void {
        this.oportunidadeService.getById(idOportunidade).subscribe({
            next: (res) => {
                const oportunidade = res.detalhe?.oportunidade;

                if (res.isError || !oportunidade) {
                    this.isLoading = false;
                    this.alertService.show(
                        'Aviso',
                        res.errorDescription || 'Não foi possível carregar a oportunidade para iniciar o documento.',
                        AlertType.warning,
                    );
                    return;
                }

                this.oportunidadeOrigem = { codigo: oportunidade.codigo, titulo: oportunidade.titulo };
                this.iniciarDaPessoa(oportunidade.idPessoaCliente, oportunidade);
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show(
                    'Aviso',
                    'Não foi possível carregar a oportunidade para iniciar o documento agora. Tente novamente em alguns instantes.',
                    AlertType.warning,
                );
            },
        });
    }

    /** Vendedor responsável (se a tela o oferece) e o nome do contato da oportunidade em "Aos cuidados de". */
    private aplicarDadosDaOportunidade(oportunidade: OportunidadeKanbanItemComValorStruct): void {
        const vendedor = this.vendedores.find((v) => v.idVendedor === oportunidade.idVendedorResponsavel);

        if (vendedor) this.form.patchValue({ idVendedor: vendedor.idVendedor });

        if (oportunidade.idPessoaContato == null) return;

        // Acessório: sem o nome do contato o campo fica vazio para digitar.
        this.pessoaLookupService.getListContatoIds([oportunidade.idPessoaContato]).subscribe({
            next: (res) => {
                const contato = (res.list ?? []).find((c) => c.id === oportunidade.idPessoaContato);

                if (!res.isError && contato?.descricao) this.form.patchValue({ aosCuidadosDe: contato.descricao });
            },
            error: () => { /* o campo fica vazio para digitar */ },
        });
    }

    /**
     * Cliente que não pode ser trocado: o definido pela oportunidade no documento novo, ou o do documento
     * gravado ligado a uma oportunidade (o servidor também recusa a troca).
     */
    public get motivoClienteBloqueado(): string | null {
        if (this.idVenda == null) {
            return this.idOportunidadeDaUrl != null && this.clienteSelecionado ? 'Cliente definido pela oportunidade.' : null;
        }

        return this.documento?.idOportunidade != null
            ? 'Com oportunidade vinculada, o cliente não muda. Desvincule antes.'
            : null;
    }

    /** Volta para a ficha da oportunidade no CRM (salvar, voltar e sair do documento iniciado por ela). */
    private voltarParaOportunidade(substituirUrl = false): void {
        this.router.navigate(['/vendas/crm'], {
            queryParams: { idOportunidade: this.idOportunidadeDaUrl },
            replaceUrl: substituirUrl,
        });
    }

    /** A URL fica só com o `tipo`, para o F5 não repetir o preenchimento. */
    private limparPessoaDaUrl(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { idPessoaCliente: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    /**
     * O documento gravado sem o que é dele: só referências estáticas (task 148656). O rateio vem
     * com os ids zerados (os valores se recalculam pelos percentuais quando o total muda).
     *
     * Recebimentos NÃO vêm (Pedro, 26/09): a cópia é reprecificada pela lista vigente, o total
     * muda, e o recebimento copiado deixaria de cobrir — o usuário lança os recebimentos da cópia.
     */
    private montarCopia(origem: VendaDetalheStruct): VendaDetalheStruct {
        const hoje = new Date();
        const emissao = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());

        return {
            ...origem,
            idVenda: 0,
            idVendaTipoDocumento: this.idVendaTipoDocumento,
            isOrigemOrcamento: false,
            dataEmissao: emissao as unknown as string,
            dataValidade: null,
            dataProximoContato: null,
            // Evento e integração do pedido: pertencem ao documento que foi despachado, não à cópia.
            dataPrevistaEntrega: null,
            dataEnvio: null,
            dataMaximaDespacho: null,
            codigoRastreamento: null,
            urlRastreamento: null,
            volumes: [],
            anexos: [],
            liberacoes: [],
            integracoes: [],
            itens: (origem.itens ?? []).map((item) => ({
                ...item,
                idVendaItem: 0,
                imposto: item.imposto ? { ...item.imposto, idVendaItemImposto: 0, idVendaItem: 0 } : null,
            })),
            pagamentos: [],
            rateioCentroCusto: (origem.rateioCentroCusto ?? []).map((rateio) => ({ ...rateio, idVendaRateioCentroCusto: 0 })),
        };
    }

    private nomeDoTipo(idVendaTipoDocumento: number): string {
        switch (idVendaTipoDocumento) {
            case VendaTipoDocumentoEnum.PedidoDeVenda: return 'Pedido de venda';
            case VendaTipoDocumentoEnum.Modelo: return 'Modelo';
            default: return 'Orçamento';
        }
    }

    // ================================================================== menu Ações (148619)

    private get podeInserir(): boolean {
        return this.authService.getUserInfos()?.listaPermissoes?.includes(PermissaoEnum.Vendas_ProcessoVenda_Inserir) ?? false;
    }

    /** O menu só existe para documento gravado — clone, modelo e histórico precisam de um id. */
    public get exibeMenuDeAcoes(): boolean {
        return this.idVenda != null && !this.isLoading;
    }

    public get rotuloClonar(): string {
        return this.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda ? 'Clonar venda' : 'Clonar orçamento';
    }

    public get podeClonar(): boolean {
        return this.podeInserir;
    }

    // ============================ cliente (§5.1) ============================

    /**
     * Só a escolha na tela preenche "Aos cuidados de" (§5.1): quando está vazio ou ainda com o contato que a escolha
     * anterior colocou — trocar de cliente troca o contato. O que foi gravado ou digitado à mão fica.
     */
    public aoEscolherCliente(): void {
        const atual = this.form.get('aosCuidadosDe')?.value;

        this.aguardaContatoDoCliente = !atual || atual === this.aosCuidadosDeAutomatico;
    }

    /** O financeiro do cliente chega depois da escolha, com os contatos (`ClienteDetalheStruct.contatos`). */
    public aoReceberClienteFinanceiro(cliente: ClienteDetalheStruct | null): void {
        this.clienteFinanceiro = cliente;

        if (!cliente || !this.aguardaContatoDoCliente) return;

        this.aguardaContatoDoCliente = false;
        const primeiroContato = cliente.contatos?.[0]?.nome ?? null;

        // Cliente novo sem contato: o contato automático do cliente anterior não pode ficar na proposta dele.
        if (!primeiroContato && (!this.aosCuidadosDeAutomatico || this.form.get('aosCuidadosDe')?.value !== this.aosCuidadosDeAutomatico)) return;

        this.form.patchValue({ aosCuidadosDe: primeiroContato });
        this.form.get('aosCuidadosDe')?.markAsDirty();
        this.aosCuidadosDeAutomatico = primeiroContato;
    }

    // ============================ exclusão (T15, §10) ============================

    public get podeExcluirDocumento(): boolean {
        return this.authService.getUserInfos()?.listaPermissoes?.includes(PermissaoEnum.Vendas_ProcessoVenda_Excluir) ?? false;
    }

    public get tooltipExcluirDocumento(): string {
        return this.podeExcluirDocumento ? '' : 'Você não tem permissão para excluir documentos.';
    }

    public get rotuloExcluir(): string {
        return this.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda ? 'Excluir venda' : 'Excluir orçamento';
    }

    /**
     * Mesma confirmação, mesmo endpoint e mesma regra do menu do card: o servidor recusa documento com nota ou
     * lançamento e explica o motivo. Excluído, a tela volta ao Processo de Venda sem perguntar pelas alterações.
     */
    public acaoExcluirDocumento(): void {
        if (this.idVenda == null || !this.podeExcluirDocumento) return;

        const rotulo = `${this.nomeDoTipoDeDocumento} nº ${formatarNumeroDocumento(this.documento?.numero ?? 0, this.idVendaTipoDocumento)}`;

        this.dialog
            .open(ConfirmModalComponent, {
                width: '440px',
                data: {
                    title: 'Excluir documento',
                    innerHtml:
                        `<p>Tem certeza que deseja excluir o <strong>${rotulo}</strong>?</p>` +
                        '<p>Ele sai do quadro e da listagem. O histórico é preservado.</p>' +
                        (this.documento?.idOportunidade != null ? `<p>${PV_TEXTO_EXCLUSAO_COM_OPORTUNIDADE}</p>` : ''),
                    buttonConfirm: 'Excluir',
                    buttonClose: 'Cancelar',
                },
            })
            .afterClosed()
            .subscribe((resultado) => {
                if (!resultado?.confirm) return;

                this.enviarExclusaoDoDocumento();
            });
    }

    private enviarExclusaoDoDocumento(): void {
        const request = new ExcluirVendaRequest();
        request.idsVenda = [this.idVenda!];

        this.processoVendaService.excluir(request).subscribe({
            next: (resposta) => {
                if (resposta.isError) {
                    this.alertService.show('Atenção', resposta.errorDescription, AlertType.warning);
                    return;
                }

                // Um documento só: a recusa por regra vem no resultado dele, com o motivo.
                const resultado = (resposta.resultados ?? [])[0];

                if (!resultado?.isExcluido) {
                    this.alertService.show('Atenção', resultado?.motivo ?? 'Documento não excluído.', AlertType.warning);
                    return;
                }

                if (resposta.avisoDescricao) this.alertService.show('Aviso', resposta.avisoDescricao, AlertType.warning);
                this.alertService.show('Sucesso', `${rotuloDocumento(resultado.numero, resultado.idVendaTipoDocumento)} excluído com sucesso.`, AlertType.success);
                // O documento não existe mais: nada a salvar, então sai direto.
                this.form.markAsPristine();
                this.sair();
            },
            error: () => this.alertService.show('Erro', 'Não foi possível falar com o servidor.', AlertType.error),
        });
    }

    /** "Criar modelo": só orçamento, em etapa do fluxo principal — a mesma regra do card e do servidor. */
    public get exibeCriarModelo(): boolean {
        return this.idVendaTipoDocumento === VendaTipoDocumentoEnum.Orcamento;
    }

    /** Modelo é base para novos orçamentos, não documento para o cliente: não gera PDF nem link. */
    public get exibeGerarPdfECompartilhar(): boolean {
        return this.idVendaTipoDocumento !== VendaTipoDocumentoEnum.Modelo;
    }

    public get podeCriarModelo(): boolean {
        return this.podeInserir && this.etapaAtual?.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal;
    }

    // ── Financeiro do pedido (T05/T08) ───────────────────────────────────────

    /** Financeiro lançado = linha de integração do tipo 1 (títulos) ou 6 (movimentações). */
    public get isFinanceiroLancado(): boolean {
        return (this.documento?.integracoes ?? []).some((i) =>
            i.idVendaTipoIntegracao === VendaTipoIntegracaoEnum.Financeiro
            || i.idVendaTipoIntegracao === VendaTipoIntegracaoEnum.FinanceiroMovimentacao);
    }

    /** Só o pedido gravado lança financeiro; o orçamento nunca gera contas (DF5). */
    public get exibeLancarFinanceiro(): boolean {
        // Pedido finalizado (Cancelado, Entregue...) não lança mais financeiro.
        return this.idVenda != null && this.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda && !this.isFinanceiroLancado
            && !this.documento?.isEtapaFinalizada;
    }

    public get exibeExcluirFinanceiro(): boolean {
        return this.idVenda != null && this.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda && this.isFinanceiroLancado;
    }

    /** Lançar usa a permissão de inserir do pedido; com alteração pendente, salva antes (o lançamento lê o gravado). */
    public get podeLancarFinanceiro(): boolean {
        return this.podeInserir && !this.temAlteracoesPendentes && !this.isSalvando;
    }

    public get tooltipLancarFinanceiro(): string {
        if (!this.podeInserir) return 'Você não tem permissão para lançar o financeiro.';
        if (this.temAlteracoesPendentes || this.isSalvando) return 'Salve as alterações do documento antes de lançar o financeiro.';
        return '';
    }

    public get podeExcluirFinanceiro(): boolean {
        return this.authService.getUserInfos()?.listaPermissoes?.includes(PermissaoEnum.Vendas_ProcessoVenda_ExcluirFinanceiro) ?? false;
    }

    public get tooltipExcluirFinanceiro(): string {
        return this.podeExcluirFinanceiro ? '' : 'Você não tem permissão para excluir o financeiro.';
    }

    public acaoLancarFinanceiro(): void {
        if (this.idVenda == null || !this.podeLancarFinanceiro) return;

        this.dialog
            .open(PvLancarFinanceiroDialogComponent, {
                data: {
                    idVenda: this.idVenda,
                    rotuloDocumento: `${this.nomeDoTipoDeDocumento} nº ${formatarNumeroDocumento(this.documento?.numero ?? 0, this.idVendaTipoDocumento)}`,
                    venda: this.documento,
                    financas: this.financas,
                } as PvLancarFinanceiroDialogData,
                width: '680px',
                maxWidth: '96vw',
                autoFocus: false,
            })
            .afterClosed()
            .subscribe((lancou: boolean | undefined) => {
                if (lancou) this.carregar();
            });
    }

    public acaoExcluirFinanceiro(): void {
        if (this.idVenda == null || !this.podeExcluirFinanceiro) return;

        this.dialog
            .open(PvExcluirFinanceiroDialogComponent, {
                data: {
                    idVenda: this.idVenda,
                    rotuloDocumento: `${this.nomeDoTipoDeDocumento} nº ${formatarNumeroDocumento(this.documento?.numero ?? 0, this.idVendaTipoDocumento)}`,
                } as PvExcluirFinanceiroDialogData,
                width: '680px',
                maxWidth: '96vw',
                autoFocus: false,
            })
            .afterClosed()
            .subscribe((excluiu: boolean | undefined) => {
                if (!excluiu) return;
                this.isLoading = true;
                this.carregar();
            });
    }

    // ── Estoque do pedido (T10/T11) ──────────────────────────────────────────

    /** Estoque lançado = linha de integração do tipo 2. */
    public get isEstoqueLancado(): boolean {
        return (this.documento?.integracoes ?? []).some((i) => i.idVendaTipoIntegracao === VendaTipoIntegracaoEnum.Estoque);
    }

    /** Só o pedido gravado lança estoque; o orçamento não movimenta estoque. */
    public get exibeLancarEstoque(): boolean {
        // Pedido finalizado (Cancelado, Entregue...) não lança mais estoque.
        return this.idVenda != null && this.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda && !this.isEstoqueLancado
            && !this.documento?.isEtapaFinalizada;
    }

    public get exibeExcluirEstoque(): boolean {
        return this.idVenda != null && this.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda && this.isEstoqueLancado;
    }

    /** Lançar usa a permissão de inserir do pedido; com alteração pendente, salva antes (o lançamento lê o gravado). */
    public get podeLancarEstoque(): boolean {
        return this.podeInserir && !this.temAlteracoesPendentes && !this.isSalvando;
    }

    public get tooltipLancarEstoque(): string {
        if (!this.podeInserir) return 'Você não tem permissão para lançar o estoque.';
        if (this.temAlteracoesPendentes || this.isSalvando) return 'Salve as alterações do documento antes de lançar o estoque.';
        return '';
    }

    public get podeExcluirEstoque(): boolean {
        return this.authService.getUserInfos()?.listaPermissoes?.includes(PermissaoEnum.Vendas_ProcessoVenda_ExcluirEstoque) ?? false;
    }

    public get tooltipExcluirEstoque(): string {
        return this.podeExcluirEstoque ? '' : 'Você não tem permissão para excluir o estoque.';
    }

    public acaoLancarEstoque(): void {
        if (this.idVenda == null || !this.podeLancarEstoque) return;

        this.dialog
            .open(PvLancarEstoqueDialogComponent, {
                data: {
                    idVenda: this.idVenda,
                    rotuloDocumento: `${this.nomeDoTipoDeDocumento} nº `
                        + `${formatarNumeroDocumento(this.documento?.numero ?? 0, this.idVendaTipoDocumento)}`,
                } as PvLancarEstoqueDialogData,
                width: '680px',
                maxWidth: '96vw',
                autoFocus: false,
            })
            .afterClosed()
            .subscribe((lancou: boolean | undefined) => {
                if (lancou) this.carregar();
            });
    }

    public acaoExcluirEstoque(): void {
        if (this.idVenda == null || !this.podeExcluirEstoque) return;

        this.dialog
            .open(PvExcluirEstoqueDialogComponent, {
                data: {
                    idVenda: this.idVenda,
                    rotuloDocumento: `${this.nomeDoTipoDeDocumento} nº `
                        + `${formatarNumeroDocumento(this.documento?.numero ?? 0, this.idVendaTipoDocumento)}`,
                } as PvExcluirEstoqueDialogData,
                width: '680px',
                maxWidth: '96vw',
                autoFocus: false,
            })
            .afterClosed()
            .subscribe((excluiu: boolean | undefined) => {
                if (!excluiu) return;
                this.isLoading = true;
                this.carregar();
            });
    }

    /** "Ver lançamentos" da faixa: títulos e movimentações do Finanças, só para consultar (permissão de ver o pedido). */
    public acaoVerLancamentos(): void {
        if (this.idVenda == null) return;

        this.dialog.open(PvFinanceiroDialogComponent, {
            data: {
                idVenda: this.idVenda,
                rotuloDocumento: `${this.nomeDoTipoDeDocumento} nº ${formatarNumeroDocumento(this.documento?.numero ?? 0, this.idVendaTipoDocumento)}`,
            } as PvFinanceiroDialogData,
            width: '680px',
            maxWidth: '96vw',
            autoFocus: false,
        });
    }

    public get tooltipAlterarEtapa(): string {
        return this.destinosDeEtapa.length ? '' : 'Não há etapa de destino habilitada a partir desta etapa.';
    }

    public acaoAlterarEtapa(): void {
        this.pedidoAbrirEtapa++;
    }

    /**
     * Clona a versão GRAVADA — com alteração pendente, a cópia não teria o que o usuário vê na
     * tela, então pede para salvar antes (a mesma regra do campo Etapa). Modelo vira orçamento.
     */
    public acaoClonar(): void {
        if (this.idVenda == null) return;

        if (this.temAlteracoesPendentes) {
            this.alertService.show('Alterações não salvas', 'Salve as alterações do documento antes de clonar.', AlertType.warning);
            return;
        }

        const tipoDestino = this.idVendaTipoDocumento === VendaTipoDocumentoEnum.Modelo
            ? VendaTipoDocumentoEnum.Orcamento
            : this.idVendaTipoDocumento;

        this.router.navigate(['/vendas/processo-venda/documento'], {
            queryParams: { tipo: tipoDestino, origem: this.idVenda },
        });
    }

    /**
     * Imprimir lê o documento GRAVADO (o servidor devolve a folha completa), então não depende de
     * Alterar nem de etapa: documento encerrado imprime. Com alteração pendente a folha divergiria
     * da tela, então pede para salvar antes.
     */
    public acaoGerarPdf(): void {
        if (this.idVenda == null) return;

        if (this.temAlteracoesPendentes) {
            this.alertService.show('Alterações não salvas', 'Salve as alterações do documento antes de gerar o PDF.', AlertType.warning);
            return;
        }

        this.dialog.open(VdImpressaoDialogComponent, {
            data: {
                idVenda: this.idVenda,
                idVendaTipoDocumento: this.idVendaTipoDocumento,
                nomeEtapa: this.etapaAtual?.nome ?? null,
                configOrcamento: this.configOrcamento,
                configPedidoVenda: this.configPedidoVenda,
                listImpostoImpressaoOrcamento: this.listImpostoImpressaoOrcamento,
                listImpostoImpressaoPedido: this.listImpostoImpressaoPedido,
                financas: this.financas,
                formasEnvio: this.formasEnvio,
            } as VdImpressaoDialogData,
            width: '960px',
            maxWidth: '96vw',
        });
    }

    /** Gerar link e enviar e-mail grava no documento (token, validade, ocorrência): exige Alterar. */
    public get podeCompartilhar(): boolean {
        return this.authService.getUserInfos()?.listaPermissoes?.includes(PermissaoEnum.Vendas_ProcessoVenda_Alterar) ?? false;
    }

    public get tooltipCompartilhar(): string {
        return this.podeCompartilhar ? '' : 'Você não tem permissão para alterar este documento.';
    }

    /**
     * Compartilha a versão GRAVADA, pela mesma razão do Clonar. O link vive na venda (token e
     * validade); o diálogo devolve o novo estado e ele entra no documento em memória sem sujar o
     * formulário — o servidor já gravou.
     */
    public acaoCompartilhar(): void {
        if (this.idVenda == null) return;

        if (this.temAlteracoesPendentes) {
            this.alertService.show('Alterações não salvas', 'Salve as alterações do documento antes de compartilhar.', AlertType.warning);
            return;
        }

        this.dialog
            .open(VdCompartilharDialogComponent, {
                data: {
                    idVenda: this.idVenda,
                    nomeDocumento: `${this.nomeDoTipoDeDocumento} nº ${formatarNumeroDocumento(this.documento?.numero ?? 0, this.idVendaTipoDocumento)}`,
                    nomeEmpresa: this.authService.getUserInfos()?.nomeEmpresa ?? '',
                    linkPublicoToken: this.documento?.linkPublicoToken ?? null,
                    linkPublicoExpiracao: this.documento?.linkPublicoExpiracao ?? null,
                    urlLinkPublico: this.documento?.urlLinkPublico ?? null,
                    emailCliente: this.clienteFinanceiro?.email ?? null,
                } as VdCompartilharDialogData,
                width: '640px',
                maxWidth: '96vw',
            })
            .afterClosed()
            .subscribe((resultado: VdCompartilharDialogResult | undefined) => {
                if (!resultado || !this.documento) return;

                this.documento.linkPublicoToken = resultado.linkPublicoToken;
                this.documento.linkPublicoExpiracao = resultado.linkPublicoExpiracao;
                this.documento.urlLinkPublico = resultado.urlLinkPublico;
            });
    }

    public acaoCriarModelo(): void {
        if (this.idVenda == null) return;

        if (this.temAlteracoesPendentes) {
            this.alertService.show('Alterações não salvas', 'Salve as alterações do documento antes de criar o modelo.', AlertType.warning);
            return;
        }

        const request = new CriarModeloVendaRequest();
        request.idVenda = this.idVenda;

        this.processoVendaService.criarModelo(request).subscribe({
            next: (res) => {
                if (res.isError) {
                    this.alertService.show('Atenção', res.errorDescription ?? '', AlertType.warning);
                    return;
                }

                const numero = formatarNumeroDocumento(res.modelo.numero, res.modelo.idVendaTipoDocumento);
                this.alertService.show('Modelo criado', `Modelo nº ${numero} criado a partir deste orçamento.`, AlertType.success);
            },
            error: (err) => this.alertService.show('Erro', err.message, AlertType.error),
        });
    }

    /** Anotar ou excluir anotação no histórico é mexer num documento que já existe: Alterar, não Inserir. */
    public get podeAlterarHistorico(): boolean {
        return this.authService.getUserInfos()?.listaPermissoes?.includes(PermissaoEnum.Vendas_ProcessoVenda_Alterar) ?? false;
    }

    /** Sem a configuração do Pedido de Venda, a exclusão da anotação fica bloqueada — o padrão dela. */
    public get bloquearExclusaoOcorrencia(): boolean {
        return this.configPedidoVenda?.isBloquearExclusaoOcorrencia ?? true;
    }

    // ================================================ oportunidade do documento (D3, RX-01 a RX-06)

    public readonly tooltipOportunidadeOutroVendedor = TOOLTIP_OPORTUNIDADE_OUTRO_VENDEDOR;

    /** Permissões das ações de oportunidade (RX-04, RX-05), as mesmas do card do Processo de Venda. */
    public get permissoesOportunidade(): PvPermissoesOportunidade {
        return resolverPermissoesOportunidade(this.authService.getUserInfos()?.listaPermissoes);
    }

    /** Modelo não tem oportunidade: no menu fica só "Ver oportunidades do cliente", que já existia. */
    public get exibeItensDeOportunidade(): boolean {
        return this.idVendaTipoDocumento !== VendaTipoDocumentoEnum.Modelo;
    }

    /** Rótulo e motivo de bloqueio de cada item, com as mesmas regras do card e da linha da lista. */
    public get acoesOportunidade(): PvAcoesOportunidadeVm {
        return acoesOportunidade(this.documento ?? {}, this.form.get('idPessoaCliente')?.value, this.permissoesOportunidade);
    }

    /** "Ver oportunidade #N": a ficha no CRM. Com alteração pendente, confirma antes de sair, como o Voltar. */
    public verOportunidade(): void {
        const idOportunidade = this.documento?.idOportunidade;
        if (idOportunidade == null) return;

        const abrir = () => this.router.navigate(['/vendas/crm'], { queryParams: { idOportunidade } });

        if (!this.temAlteracoesPendentes) {
            abrir();
            return;
        }

        this.dialog
            .open(ConfirmModalComponent, {
                width: '440px',
                data: {
                    title: 'Sair sem salvar?',
                    description: 'As alterações não salvas serão perdidas. Sair mesmo assim?',
                    buttonConfirm: 'Sair',
                    buttonClose: 'Continuar editando',
                },
            })
            .afterClosed()
            .subscribe((resultado) => {
                if (resultado?.confirm) abrir();
            });
    }

    /**
     * "Criar oportunidade" (RX-01): usa o cliente e o vendedor GRAVADOS, que são os que o servidor confere;
     * com alteração pendente, pede para salvar antes, como Clonar e Criar modelo.
     */
    public criarOportunidade(): void {
        const documento = this.documento;
        if (this.idVenda == null || !documento?.idPessoaCliente) return;

        if (this.temAlteracoesPendentes) {
            this.alertService.show('Aviso', 'Salve as alterações do documento antes de criar a oportunidade.', AlertType.warning);
            return;
        }

        this.dialog
            .open(CriarOportunidadeDocumentoDialogComponent, {
                width: '700px',
                maxWidth: '95vw',
                autoFocus: false,
                data: {
                    idVenda: this.idVenda,
                    idVendaTipoDocumento: documento.idVendaTipoDocumento,
                    numero: documento.numero,
                    idPessoaCliente: documento.idPessoaCliente,
                    nomeCliente: documento.nomePessoaCliente ?? '',
                    idVendedor: documento.idVendedor ?? null,
                    podeVincular: !this.permissoesOportunidade.motivoBloqueioVinculo,
                } as CriarOportunidadeDocumentoDialogData,
            })
            .afterClosed()
            .subscribe((resultado: CriarOportunidadeDocumentoDialogResult | undefined) => {
                if (resultado?.idOportunidadeParaVincular != null) {
                    this.vincularOportunidade(resultado.idOportunidadeParaVincular);
                    return;
                }

                if (!resultado?.criada || !this.documento) return;

                // A etapa não volta na criação; o menu só precisa do vínculo e do número.
                this.documento.idOportunidade = resultado.criada.idOportunidade;
                this.documento.codigoOportunidade = resultado.criada.codigo;
                this.documento.tituloOportunidade = resultado.criada.titulo;
                this.documento.nomeEtapaOportunidade = null;
                this.documento.isOportunidadeForaDoEscopo = false;
            });
    }

    /** "Vincular a oportunidade" (RX-02): as abertas do cliente gravado, dentro do escopo. */
    public vincularOportunidade(idOportunidadeSugerida: number | null = null): void {
        const documento = this.documento;
        if (this.idVenda == null || !documento?.idPessoaCliente) return;

        if (this.temAlteracoesPendentes) {
            this.alertService.show('Aviso', 'Salve as alterações do documento antes de vincular a oportunidade.', AlertType.warning);
            return;
        }

        this.dialog
            .open(VincularOportunidadeDialogComponent, {
                width: '700px',
                maxWidth: '95vw',
                autoFocus: false,
                data: {
                    idVenda: this.idVenda,
                    idVendaTipoDocumento: documento.idVendaTipoDocumento,
                    numero: documento.numero,
                    idPessoaCliente: documento.idPessoaCliente,
                    nomeCliente: documento.nomePessoaCliente ?? '',
                    idOportunidadeSugerida,
                } as VincularOportunidadeDialogData,
            })
            .afterClosed()
            .subscribe((resultado: VincularOportunidadeDialogResult | undefined) => {
                if (!resultado || !this.documento) return;

                // O cliente passa a ficar travado pelo vínculo (motivoClienteBloqueado).
                this.documento.idOportunidade = resultado.oportunidade.idOportunidade;
                this.documento.codigoOportunidade = resultado.oportunidade.codigo;
                this.documento.tituloOportunidade = resultado.oportunidade.titulo;
                this.documento.nomeEtapaOportunidade = resultado.oportunidade.nomeEtapa;
                this.documento.isOportunidadeForaDoEscopo = false;
            });
    }

    /** "Desvincular da oportunidade" (RX-03): confirma no dialog; o cliente volta a poder ser trocado. */
    public desvincularOportunidade(): void {
        const documento = this.documento;
        if (this.idVenda == null || documento?.idOportunidade == null) return;

        this.dialog
            .open(DesvincularOportunidadeDialogComponent, {
                width: '480px',
                maxWidth: '95vw',
                autoFocus: false,
                data: {
                    idVenda: this.idVenda,
                    idVendaTipoDocumento: documento.idVendaTipoDocumento,
                    numero: documento.numero,
                    codigoOportunidade: documento.codigoOportunidade ?? '',
                } as DesvincularOportunidadeDialogData,
            })
            .afterClosed()
            .subscribe((desvinculou: boolean | undefined) => {
                if (!desvinculou || !this.documento) return;

                this.documento.idOportunidade = null;
                this.documento.codigoOportunidade = null;
                this.documento.tituloOportunidade = null;
                this.documento.nomeEtapaOportunidade = null;
                this.documento.isOportunidadeForaDoEscopo = null;
            });
    }

    /** "Ver oportunidades do cliente": o dialog trata permissão e navegação sozinho. */
    public verOportunidadesCliente(): void {
        const idPessoaCliente = this.form.get('idPessoaCliente')?.value;
        if (!idPessoaCliente) return;

        this.dialog.open(OportunidadesClienteDialogComponent, {
            width: '1040px',
            maxWidth: '96vw',
            maxHeight: '90vh',
            autoFocus: false,
            data: {
                idPessoaCliente,
                nomeCliente: this.clienteSelecionado?.nome ?? '',
                titulo: 'Oportunidades do cliente',
                origem: 'documento-venda',
            } as OportunidadesClienteDialogData,
        });
    }

    public abrirHistorico(): void {
        if (this.idVenda == null) return;

        this.dialog
            .open(PvHistoricoDialogComponent, {
                data: {
                    idVenda: this.idVenda,
                    rotuloDocumento: this.titulo,
                    podeAlterar: this.podeAlterarHistorico,
                    bloquearExclusaoOcorrencia: this.bloquearExclusaoOcorrencia,
                } as PvHistoricoDialogData,
                width: '720px',
            })
            .afterClosed()
            // A seção Histórico mostra o mesmo histórico, que pode ter mudado no modal.
            .subscribe(() => this.pedidoRecarregarHistorico++);
    }

    /**
     * Sair da tela. Com alteracao pendente, confirma antes (§9.1): "As alteracoes nao salvas
     * serao perdidas. Sair mesmo assim?" — nao existe rascunho automatico.
     */
    public voltar(): void {
        if (!this.temAlteracoesPendentes) {
            this.sair();
            return;
        }

        this.dialog
            .open(ConfirmModalComponent, {
                width: '440px',
                data: {
                    title: 'Sair sem salvar?',
                    description: 'As alterações não salvas serão perdidas. Sair mesmo assim?',
                    buttonConfirm: 'Sair',
                    buttonClose: 'Continuar editando',
                },
            })
            .afterClosed()
            .subscribe((resultado) => {
                if (resultado?.confirm) this.sair();
            });
    }

    private sair(): void {
        if (this.idOportunidadeDaUrl != null) {
            this.voltarParaOportunidade();
            return;
        }

        this.router.navigate(['/vendas/processo-venda']);
    }
}
